import React, { useCallback, useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import yaml from 'js-yaml';
import {
  CognitoUserPool
} from 'amazon-cognito-identity-js';
import LicenseRequest from './components/LicenseRequest';
import ReadmeEditor from './components/ReadmeEditor';
import RawYamlEditor from './components/RawYamlEditor';
import { API_URL, USER_POOL_ID, USER_POOL_CLIENT_ID } from './config';
import { formatDateTime } from './utils/dateFormat';
import { udiFor } from './utils/udi';

// Valid countries for the customer-details Country dropdown (UN members + common territories),
// sorted alphabetically. Stored verbatim as the country string.
const COUNTRIES = [
  'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Antigua and Barbuda', 'Argentina',
  'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados',
  'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana',
  'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi', 'Cabo Verde', 'Cambodia', 'Cameroon',
  'Canada', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo (Brazzaville)',
  'Congo (Kinshasa)', 'Costa Rica', "Côte d'Ivoire", 'Croatia', 'Cuba', 'Cyprus', 'Czechia', 'Denmark',
  'Djibouti', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea',
  'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia', 'Fiji', 'Finland', 'France', 'Gabon', 'Gambia', 'Georgia',
  'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana', 'Haiti',
  'Honduras', 'Hong Kong', 'Hungary', 'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel',
  'Italy', 'Jamaica', 'Japan', 'Jordan', 'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
  'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
  'Macao', 'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania',
  'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique',
  'Myanmar', 'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria',
  'North Korea', 'North Macedonia', 'Norway', 'Oman', 'Pakistan', 'Palau', 'Palestine', 'Panama',
  'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Qatar', 'Romania', 'Russia',
  'Rwanda', 'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino',
  'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore',
  'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain',
  'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria', 'Taiwan', 'Tajikistan', 'Tanzania',
  'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan',
  'Tuvalu', 'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay',
  'Uzbekistan', 'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam', 'Yemen', 'Zambia', 'Zimbabwe',
];

const userPool = new CognitoUserPool({
  UserPoolId: USER_POOL_ID,
  ClientId: USER_POOL_CLIENT_ID,
});
console.log('CognitoUserPool config:', USER_POOL_ID, USER_POOL_CLIENT_ID);

function getCurrentSession() {
  return new Promise((resolve, reject) => {
    const cognitoUser = userPool.getCurrentUser();
    if (!cognitoUser) return reject('No user');
    cognitoUser.getSession((err, session) => {
      if (err) return reject(err);
      resolve(session);
    });
  });
}

// "Opens in a new tab" affordance — a box with an arrow pointing to the top-right.
function ExternalLinkIcon() {
  return (
    <svg
      width="13"
      height="13"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0 }}
      aria-hidden="true"
    >
      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
      <polyline points="15 3 21 3 21 9" />
      <line x1="10" y1="14" x2="21" y2="3" />
    </svg>
  );
}

// Decode JWT token to get user groups
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(function (c) {
      return '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2);
    }).join(''));
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return {};
  }
}

// Stable release tags (vX.Y.Z) sorted newest first. Distributors may pick from
// the DISTRIBUTOR_RELEASE_CHOICES most recent; admins see every tag.
const DISTRIBUTOR_RELEASE_CHOICES = 3;
const STABLE_TAG = /^v(\d+)\.(\d+)\.(\d+)$/;
function sortStableReleases(tags) {
  const parse = (t) => t.match(STABLE_TAG).slice(1, 4).map(Number);
  return (tags || [])
    .filter((t) => STABLE_TAG.test(t))
    .sort((a, b) => {
      const av = parse(a); const bv = parse(b);
      for (let i = 0; i < 3; i++) if (av[i] !== bv[i]) return bv[i] - av[i];
      return 0;
    });
}

// Grafana admin credentials the Rho chart falls back to when rho.yaml sets none.
// Source of truth: rho-product/manifests/values.yaml (services.grafana.credentials).
const CHART_DEFAULT_GRAFANA = { username: 'admin', password: '16Bytes!' };

export default function RepoConfig() {
  const { repo } = useParams();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [fields, setFields] = useState(null);
  const [rawYaml, setRawYaml] = useState('');
  const [saving, setSaving] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const [releases, setReleases] = useState([]);
  const [releasesLoading, setReleasesLoading] = useState(true);
  const [validationErrors, setValidationErrors] = useState({});
  const [latestRelease, setLatestRelease] = useState(null);
  const [deployKeys, setDeployKeys] = useState([]);
  const [deployKeysLoading, setDeployKeysLoading] = useState(false);
  const [newDeployKey, setNewDeployKey] = useState({ title: '', key: '' });
  const [deployKeyError, setDeployKeyError] = useState(null);
  const [deployKeySuccess, setDeployKeySuccess] = useState(null);
  const [configurationSaved, setConfigurationSaved] = useState(false);
  const [configurationExplicitlySaved, setConfigurationExplicitlySaved] = useState(false);
  const [savedFieldValues, setSavedFieldValues] = useState(null);
  const [ghcrKey, setGhcrKey] = useState('');
  const [ghcrKeyLoading, setGhcrKeyLoading] = useState(true);
  const [userOrganizationId, setUserOrganizationId] = useState(null);
  const [showGrafanaPassword, setShowGrafanaPassword] = useState(false);
  const [activeTab, setActiveTab] = useState('customer');
  const emptyCustomerForm = { contact_name: '', institution: '', address_line1: '', address_line2: '', address_line3: '', city: '', state_province: '', postal_code: '', country: '', phone: '', email: '', additional_details: '' };
  const [customerDetails, setCustomerDetails] = useState(null);
  const [customerForm, setCustomerForm] = useState(emptyCustomerForm);
  const [customerLoading, setCustomerLoading] = useState(true);
  const [customerSaving, setCustomerSaving] = useState(false);
  const [customerError, setCustomerError] = useState(null);
  const [customerSavedMsg, setCustomerSavedMsg] = useState(null);
  const [customerValidationErrors, setCustomerValidationErrors] = useState({});
  // Archive state — an archived repo is frozen (read-only) across all tabs.
  const [archived, setArchived] = useState(false);
  const [archivedAt, setArchivedAt] = useState(null);
  const [archivedBy, setArchivedBy] = useState(null);
  const [showArchiveModal, setShowArchiveModal] = useState(false);
  const [archiving, setArchiving] = useState(false);
  const [archiveError, setArchiveError] = useState(null);
  const navigate = useNavigate();

  const customerRequiredFields = ['contact_name', 'institution', 'address_line1', 'city', 'state_province', 'postal_code', 'country', 'phone', 'email'];

  const [configAudit, setConfigAudit] = useState([]);
  const [configAuditLoading, setConfigAuditLoading] = useState(false);
  const [auditVersionOnly, setAuditVersionOnly] = useState(true);

  // ---- Version documentation (user manual / release notes) ----
  const [versionDocs, setVersionDocs] = useState({ releaseNotes: [], manuals: [] });
  const [versionDocsLoading, setVersionDocsLoading] = useState(false);

  // ---- Post-Installation tab state ----
  const POST_INSTALL_CONFIGURABLES = [
    { key: 'trial_end_date', label: 'Trial period end date', desc: 'Contact 16 Bit if a trial is requested.', dbVar: 'trial_end_date', defaultValue: '', type: 'date' },
    { key: 'analytics_dxa_followup_timeframe_in_months', label: 'Delta T (months)', desc: 'DXA considered Rho-triggered if within X months of x-ray.', dbVar: 'analytics_dxa_followup_timeframe_in_months', defaultValue: '12', type: 'number' },
    { key: 'dxa_reporting', label: 'DXA Reporting enabled', desc: 'Optional feature, jurisdiction- and contract-dependent.', dbVar: 'dxa_reporting (RHO_CONFIGURATION)', defaultValue: false, type: 'boolean' },
    { key: 'dicom_tags', label: 'DICOM tags used in filters', desc: 'Custom DICOM tags used in filter criteria.', dbVar: 'DICOM_TAG table', defaultValue: 'StudyDescription, ModalitiesInStudy, ViewPosition, AccessionNumber', type: 'text' },
    { key: 'filter_criteria', label: 'Filter criteria (per body part)', desc: 'Combination of DICOM tags per body part.', dbVar: 'model_map', defaultValue: '', type: 'textarea' },
    { key: 't_pull', label: 'Pull frequency (seconds)', desc: 'How often Rho checks for new x-rays/DXAs.', dbVar: 't_pull', defaultValue: '60', type: 'number' },
    { key: 'dicom_age_filter_min', label: 'Min patient age', desc: 'Cannot be less than 50.', dbVar: 'dicom_age_filter (min)', defaultValue: '50', type: 'number' },
    { key: 'dicom_age_filter_max', label: 'Max patient age', desc: '', dbVar: 'dicom_age_filter (max)', defaultValue: '110', type: 'number' },
    { key: 'qc', label: 'Perform image QC', desc: 'QC runs before BMD inference.', dbVar: 'qc', defaultValue: true, type: 'boolean' },
    { key: 'prev_dxa_scan_duration_in_months', label: 'Skip if recent DXA within (months)', desc: 'Skip x-ray if patient had a DXA within last X months.', dbVar: 'prev_dxa_scan_duration_in_months', defaultValue: '24', type: 'number' },
    { key: 'rho_score_threshold', label: 'Rho score threshold (non-US only)', desc: 'Threshold for "Rho Positive" determination. Default >5 non-US, >6 US.', dbVar: '(set in Rho Settings UI)', defaultValue: '5', type: 'number' },
    { key: 'generate_all_reports', label: 'Generate All Reports', desc: 'If true, generate reports for every case, not only Rho-Positive.', dbVar: 'generate_all_reports', defaultValue: false, type: 'boolean' },
    { key: 'customer_specific_notes_heading', label: 'Custom notes heading', desc: 'Heading for the customer-specific section of the Rho report.', dbVar: 'customer_specific_notes_heading (CUSTOMER table)', defaultValue: '', type: 'text' },
    { key: 'customer_specific_notes_content', label: 'Custom notes content', desc: 'Body text for the customer-specific section.', dbVar: 'customer_specific_notes_content (CUSTOMER table)', defaultValue: '', type: 'textarea' },
    { key: 'send_to_pacs', label: 'Send reports to PACS', desc: 'Check if institution wants reports delivered to PACS.', dbVar: 'send_to_pacs', defaultValue: false, type: 'boolean' },
    { key: 'report_type', label: 'Report Type', desc: 'e.g., CLINIC_DARK, CLINIC_LIGHT.', dbVar: 'report_type', defaultValue: 'CLINIC_DARK', type: 'text' },
    { key: 'rho_report_file_format', label: 'Report file format', desc: 'png or pdf.', dbVar: 'rho_report_file_format', defaultValue: 'png', type: 'text' },
    { key: 'analytics_pull', label: 'Analytics pull', desc: 'Whether DXAs are actually pulled during analytics.', dbVar: 'analytics_pull', defaultValue: false, type: 'boolean' },
    { key: 'report_check_patient', label: 'Report Check — per patient', desc: 'true = one report per patient over X days; false = one per accession.', dbVar: 'report_check.patient', defaultValue: false, type: 'boolean' },
    { key: 'report_check_span', label: 'Report Check — span (days)', desc: 'Days when per-patient reporting.', dbVar: 'report_check.span', defaultValue: '1', type: 'number' },
    { key: 'lumbar_contiguous', label: 'DXA: require lumbar selection contiguous', desc: 'Only use contiguous vertebrae sequences (DXA Reporting only).', dbVar: 'dxa_reporting_config.lumbar_contiguous', defaultValue: false, type: 'boolean' },
    { key: 'lumbar_t_score_threshold', label: 'DXA: outlier lumbar T-score threshold', desc: 'Threshold for excluding outlier vertebrae (DXA Reporting only).', dbVar: 'dxa_reporting_config.lumbar_t_score_threshold', defaultValue: '1.5', type: 'number' },
    { key: 'precision_loss_right_hip', label: 'DXA: precision loss — right hip (%)', desc: '', dbVar: 'precision_loss_percent_thresholds.right_hip', defaultValue: '5', type: 'number' },
    { key: 'precision_loss_left_hip', label: 'DXA: precision loss — left hip (%)', desc: '', dbVar: 'precision_loss_percent_thresholds.left_hip', defaultValue: '5', type: 'number' },
    { key: 'precision_loss_lumbar', label: 'DXA: precision loss — lumbar (%)', desc: '', dbVar: 'precision_loss_percent_thresholds.lumbar', defaultValue: '5', type: 'number' },
  ];

  const buildPostInstallDefaults = () => {
    const configurables = {};
    POST_INSTALL_CONFIGURABLES.forEach((c) => { configurables[c.key] = c.defaultValue; });
    return {
      cfindIp: '', cfindPort: '', cfindAet: '',
      cstoreIp: '', cstorePort: '', cstoreAet: '',
      operationMode: '',   // 'pull' | 'push'
      testSuccessful: false,
      goLive: '',          // 'now' | 'later'
      powerscribeDiscussed: false,
      grafanaAlertAdded: false,
      trainingEmailSent: false,
      notes: '',
      configurables,
    };
  };

  const [postInstall, setPostInstall] = useState(null);
  const [postInstallForm, setPostInstallForm] = useState(buildPostInstallDefaults());
  const [postInstallLoading, setPostInstallLoading] = useState(true);
  const [postInstallSaving, setPostInstallSaving] = useState(false);
  const [postInstallError, setPostInstallError] = useState(null);
  const [postInstallSavedMsg, setPostInstallSavedMsg] = useState(null);
  const [copyEmailFeedback, setCopyEmailFeedback] = useState('');

  // ---- Service tab state ----
  const [serviceLog, setServiceLog] = useState([]);
  const [serviceLogLoading, setServiceLogLoading] = useState(false);
  const [serviceForm, setServiceForm] = useState({ first_name: '', last_name: '', notes: '' });
  const [serviceSaving, setServiceSaving] = useState(false);
  const [serviceError, setServiceError] = useState(null);
  const [serviceSavedMsg, setServiceSavedMsg] = useState(null);
  // Identity from the Cognito ID token (set once on mount). Used to pre-fill name.
  const [currentUserEmail, setCurrentUserEmail] = useState('');
  const [currentUserClaimNames, setCurrentUserClaimNames] = useState({ first: '', last: '' });
  // A record is "complete" when every required field is present. Legacy records
  // stored a single `address` string instead of the split fields — treat that as
  // satisfying the address requirements so existing customers aren't flagged.
  const customerDetailsComplete = (() => {
    if (!customerDetails) return false;
    const hasLegacyAddress = !!(customerDetails.address && customerDetails.address.toString().trim());
    return customerRequiredFields.every((k) => {
      const filled = customerDetails[k] && customerDetails[k].toString().trim().length > 0;
      if (filled) return true;
      // Address sub-fields are covered by a legacy single-line address if present.
      if (['address_line1', 'city', 'state_province', 'postal_code'].includes(k)) return hasLegacyAddress;
      return false;
    });
  })();

  // Comprehensive timezone list
  const timezones = [
    'America/New_York',
    'America/Chicago',
    'America/Denver',
    'America/Los_Angeles',
    'America/Toronto',
    'America/Vancouver',
    'America/Edmonton',
    'America/Winnipeg',
    'America/Halifax',
    'America/St_Johns',
    'Europe/London',
    'Europe/Paris',
    'Europe/Berlin',
    'Europe/Rome',
    'Europe/Madrid',
    'Europe/Amsterdam',
    'Europe/Brussels',
    'Europe/Vienna',
    'Europe/Zurich',
    'Europe/Stockholm',
    'Europe/Oslo',
    'Europe/Copenhagen',
    'Europe/Helsinki',
    'Europe/Warsaw',
    'Europe/Prague',
    'Europe/Budapest',
    'Europe/Bucharest',
    'Europe/Sofia',
    'Europe/Athens',
    'Europe/Istanbul',
    'Europe/Moscow',
    'Asia/Tokyo',
    'Asia/Shanghai',
    'Asia/Seoul',
    'Asia/Singapore',
    'Asia/Hong_Kong',
    'Asia/Taipei',
    'Asia/Bangkok',
    'Asia/Jakarta',
    'Asia/Manila',
    'Asia/Kolkata',
    'Asia/Dubai',
    'Asia/Tehran',
    'Asia/Jerusalem',
    'Australia/Sydney',
    'Australia/Melbourne',
    'Australia/Brisbane',
    'Australia/Perth',
    'Australia/Adelaide',
    'Pacific/Auckland',
    'Pacific/Fiji',
    'Pacific/Honolulu',
    'UTC'
  ];

  const handleSignOut = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) cognitoUser.signOut();
    navigate('/');
  };

  // Helper: extract editable fields from YAML object
  function extractFields(doc) {
    const helm = doc?.spec?.source?.helm || {};
    const values = helm.valuesObject || {};
    const jobs = values.jobs || {};
    const ingress = values.ingress || {};
    const pause = values.pause || {};

    // Strip .nip.io from host field if present
    let hostValue = values.host || '';
    if (hostValue.endsWith('.nip.io')) {
      hostValue = hostValue.replace('.nip.io', '');
    }

    // Get browser timezone as default
    const browserTimezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

    // Pre-populate name with repo minus prefix if not set or if set to <cluster-name>
    let nameValue = values.name || '';
    if (!nameValue || nameValue === '<cluster-name>') {
      if (repo && repo.startsWith('rho-customer-')) {
        nameValue = repo.replace('rho-customer-', '');
      }
    }

    // Keep existing targetRevision if it's already set, only filter out non-stable versions
    let targetRevisionValue = doc?.spec?.source?.targetRevision || '';
    if (targetRevisionValue && !targetRevisionValue.match(/^v\d+\.\d+\.\d+$/)) {
      targetRevisionValue = ''; // Reset non-stable versions to empty so default can be applied
    }

    return {
      // Basic configuration
      targetRevision: targetRevisionValue,
      name: nameValue,
      jurisdiction: values.jurisdiction || '',
      useCase: values.useCase || '',
      tz: values.tz || browserTimezone,
      stage: values.stage || '',
      device: values.device || '',
      host: hostValue,
      license: values.license || '',
      dicomAet: values.services?.orthanc?.dicomAet || 'rhopacs',

      // Jobs configuration
      cleanOrthancSchedule: jobs.cleanOrthanc?.schedule || '',
      backupDBSchedule: jobs.backupDB?.schedule || '',
      backupDBExpiryDays: jobs.backupDB?.expiryDays || 30,
      backupDBHostPath: jobs.backupDB?.hostPath || '',

      // Ingress configuration
      ingressFrontend: ingress.frontend || false,
      ingressBackend: ingress.backend || false,
      ingressOrthanc: ingress.orthanc || false,
      ingressPgadmin: ingress.pgadmin || false,
      ingressDicom: ingress.dicom || false,
      ingressDb: ingress.db || false,
      ingressRabbitmq: ingress.rabbitmq || false,
      ingressGrafana: ingress.grafana || false,
      ingressPrometheus: ingress.prometheus || false,

      // Pause configuration
      pauseIngestion: pause.ingestion || false,
      pauseAnalytics: pause.analytics || false,
      pauseDtp: pause.dtp || false,
      pauseGrafana: pause.grafana !== undefined ? pause.grafana : false,
      pausePrometheus: pause.prometheus !== undefined ? pause.prometheus : false,

      // Grafana credentials. When rho.yaml has none, the chart defaults are shown
      // (they are what the deployment actually uses); grafanaCredentialsInYaml
      // records whether the file carried its own block so a save does not inject
      // the defaults into every repo. See ADJUDICATION S3-13.
      grafanaCredentialsInYaml: !!(values.services?.grafana?.credentials?.username || values.services?.grafana?.credentials?.password),
      grafanaUsername: values.services?.grafana?.credentials?.username || CHART_DEFAULT_GRAFANA.username,
      grafanaPassword: values.services?.grafana?.credentials?.password || CHART_DEFAULT_GRAFANA.password,
    };
  }

  // Helper: update YAML object with form fields
  function updateYamlObj(doc, newFields) {
    // Update targetRevision
    if (!doc.spec) doc.spec = {};
    if (!doc.spec.source) doc.spec.source = {};
    doc.spec.source.targetRevision = newFields.targetRevision;

    // Update helm values
    if (!doc.spec.source.helm) doc.spec.source.helm = {};
    if (!doc.spec.source.helm.valuesObject) doc.spec.source.helm.valuesObject = {};

    const values = doc.spec.source.helm.valuesObject;

    // Basic configuration
    values.name = newFields.name;
    values.jurisdiction = newFields.jurisdiction;
    values.useCase = newFields.useCase;
    values.tz = newFields.tz;
    values.stage = newFields.stage || (isAdmin ? 'test' : 'prod'); // Use selected stage or default
    values.device = newFields.device;
    // Append .nip.io to host for backend compatibility
    values.host = newFields.host ? `${newFields.host}.nip.io` : '';
    // The license input is admin-only, so for distributors fields.license just
    // round-trips the value loaded from the YAML (or one set via License Management)
    // — writing it back preserves the license instead of wiping it on save.
    values.license = newFields.license ?? '';

    // Preserve existing services configuration (including hostPath settings)
    // This ensures that services.db, services.orthanc, services.pgadmin, and services.rabbitmq
    // configurations with their hostPath, credentials, and storage settings are maintained
    const existingServices = doc.spec?.source?.helm?.valuesObject?.services;
    if (existingServices) {
      values.services = existingServices;
    }

    // Services configuration (admin only) - update AFTER preserving existing services
    if (isAdmin) {
      if (!values.services) values.services = {};
      if (!values.services.orthanc) values.services.orthanc = {};
      // Ensure dicomAet is saved as a quoted string in YAML
      values.services.orthanc.dicomAet = String(newFields.dicomAet);
    }

    // Jobs configuration (admin only)
    if (isAdmin) {
      if (!values.jobs) values.jobs = {};
      values.jobs.cleanOrthanc = { schedule: newFields.cleanOrthancSchedule };
      values.jobs.backupDB = {
        schedule: newFields.backupDBSchedule,
        expiryDays: parseInt(newFields.backupDBExpiryDays) || 30
      };
      // Preserve hostPath if it exists
      if (newFields.backupDBHostPath) {
        values.jobs.backupDB.hostPath = newFields.backupDBHostPath;
      }
    }

    // Ingress configuration (admin only)
    if (isAdmin) {
      values.ingress = {
        frontend: newFields.ingressFrontend,
        backend: newFields.ingressBackend,
        orthanc: newFields.ingressOrthanc,
        pgadmin: newFields.ingressPgadmin,
        dicom: newFields.ingressDicom,
        db: newFields.ingressDb,
        rabbitmq: newFields.ingressRabbitmq,
        grafana: newFields.ingressGrafana,
        prometheus: newFields.ingressPrometheus,
      };
    }

    // Pause configuration (admin only)
    if (isAdmin) {
      values.pause = {
        ingestion: newFields.pauseIngestion,
        analytics: newFields.pauseAnalytics,
        dtp: newFields.pauseDtp,
        grafana: newFields.pauseGrafana,
        prometheus: newFields.pausePrometheus,
      };
    }

    // Grafana credentials (admin and distributor). Written only when rho.yaml
    // already carried a credentials block or the user changed them from the
    // chart defaults, so a routine save never writes hardcoded credentials
    // into a file that did not have them (S3-13).
    const grafanaChanged =
      newFields.grafanaUsername !== CHART_DEFAULT_GRAFANA.username ||
      newFields.grafanaPassword !== CHART_DEFAULT_GRAFANA.password;
    if (newFields.grafanaCredentialsInYaml || grafanaChanged) {
      if (!values.services) values.services = {};
      if (!values.services.grafana) values.services.grafana = {};
      if (!values.services.grafana.credentials) values.services.grafana.credentials = {};
      values.services.grafana.credentials.username = newFields.grafanaUsername;
      values.services.grafana.credentials.password = newFields.grafanaPassword;
    }

    return doc;
  }

  // Handle license generation
  const handleLicenseGenerated = (licenseKey) => {
    if (licenseKey && licenseKey.trim() !== '') {
      setFields(prev => ({ ...prev, license: licenseKey }));
    } else {
      // Clear license field if it's empty or null
      setFields(prev => {
        const newFields = { ...prev };
        delete newFields.license;
        return newFields;
      });
    }
  };

  // Deploy key management functions
  const fetchDeployKeys = async () => {
    try {
      setDeployKeysLoading(true);
      const session = await getCurrentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_URL}/deploy-keys?repo=${repo}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (response.ok) {
        const data = await response.json();
        setDeployKeys(data.keys || []);
      } else {
        console.error('Failed to fetch deploy keys:', response.statusText);
        setDeployKeys([]);
      }
    } catch (error) {
      console.error('Error fetching deploy keys:', error);
      setDeployKeys([]);
    } finally {
      setDeployKeysLoading(false);
    }
  };

  const addDeployKey = async () => {
    if (!newDeployKey.title.trim() || !newDeployKey.key.trim()) {
      setDeployKeyError('Please provide both a title and SSH public key');
      return;
    }

    try {
      setDeployKeyError(null);
      setDeployKeySuccess(null);

      const session = await getCurrentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_URL}/deploy-keys`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          repo,
          title: newDeployKey.title.trim(),
          key: newDeployKey.key.trim()
        })
      });

      if (response.ok) {
        setDeployKeySuccess('Deploy key added successfully!');
        setNewDeployKey({ title: '', key: '' });
        await fetchDeployKeys(); // Refresh the list
      } else {
        const errorData = await response.json();
        setDeployKeyError(errorData.error || 'Failed to add deploy key');
      }
    } catch (error) {
      console.error('Error adding deploy key:', error);
      setDeployKeyError('Failed to add deploy key. Please try again.');
    }
  };

  const removeDeployKey = async (keyId) => {
    if (!window.confirm('Are you sure you want to remove this deploy key?')) {
      return;
    }

    try {
      setDeployKeyError(null);
      setDeployKeySuccess(null);

      const session = await getCurrentSession();
      const token = session.getIdToken().getJwtToken();

      const response = await fetch(`${API_URL}/deploy-keys`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          repo,
          keyId
        })
      });

      if (response.ok) {
        setDeployKeySuccess('Deploy key removed successfully!');
        await fetchDeployKeys(); // Refresh the list
      } else {
        const errorData = await response.json();
        setDeployKeyError(errorData.error || 'Failed to remove deploy key');
      }
    } catch (error) {
      console.error('Error removing deploy key:', error);
      setDeployKeyError('Failed to remove deploy key. Please try again.');
    }
  };

  // Fetch user's organization and GHCR key
  const fetchUserOrganizationAndGhcrKey = async () => {
    setGhcrKeyLoading(true);
    try {
      const session = await getCurrentSession();
      const token = session.getIdToken().getJwtToken();

      // Get user's organizations
      const orgsResponse = await fetch(`${API_URL}/organizations`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (orgsResponse.ok) {
        const orgsData = await orgsResponse.json();
        const orgList = Array.isArray(orgsData) ? orgsData : (orgsData && Array.isArray(orgsData.organizations) ? orgsData.organizations : []);

        if (orgList.length > 0) {
          // For @16bit.ai users (admins), prefer the 16bit organization
          // For others, use the first organization they belong to
          let selectedOrg = orgList[0];

          const decodedToken = decodeJWT(token);
          const userEmail = decodedToken.email || '';

          if (userEmail.endsWith('@16bit.ai')) {
            // Admin user - find the 16bit organization
            const sixteenBitOrg = orgList.find(org => org.name === '16bit');
            if (sixteenBitOrg) {
              selectedOrg = sixteenBitOrg;
            }
          }

          const orgId = selectedOrg.organization_id || selectedOrg.id;
          setUserOrganizationId(orgId);

          // Fetch GHCR key for this organization
          const ghcrResponse = await fetch(`${API_URL}/organizations/${orgId}/ghcr-key`, {
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });

          if (ghcrResponse.ok) {
            const ghcrData = await ghcrResponse.json();
            setGhcrKey(ghcrData.ghcr_key || '');
          }
        }
      }
    } catch (error) {
      console.error('Error fetching GHCR key:', error);
    } finally {
      setGhcrKeyLoading(false);
    }
  };

  // Fetch rho.yaml. Exposed as a callback so the raw YAML editor can force the
  // Configuration form to re-sync after a direct edit (avoids a stale-overwrite clash).
  const fetchYaml = useCallback(async ({ silent = false } = {}) => {
    // `silent` skips the loading flag so an in-place re-sync (e.g. after a raw
    // YAML save) doesn't unmount the active tab.
    if (!silent) setLoading(true);
    setError(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();

      // Decode JWT to get user groups
      const decodedToken = decodeJWT(idToken);
      const groups = decodedToken['cognito:groups'] || [];
      const adminStatus = groups.includes('Admin');
      setIsAdmin(adminStatus);

      // Fetch user's organization and GHCR key
      fetchUserOrganizationAndGhcrKey();

      const response = await fetch(`${API_URL}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: {
          Authorization: idToken,
        },
      });
      if (!response.ok) throw new Error(await response.text());
      const yamlText = await response.text();
      setRawYaml(yamlText);
      const doc = yaml.load(yamlText);
      const extractedFields = extractFields(doc);
      setFields(extractedFields);

      // If this is an existing configuration with valid fields, mark it as explicitly saved
      const requiredFields = ['targetRevision', 'name', 'jurisdiction', 'tz', 'stage', 'device', 'host'];
      const allFieldsValid = requiredFields.every(field => {
        const value = extractedFields[field];
        if (typeof value === 'boolean') return true;
        return value && value.toString().trim().length > 0;
      });

      if (allFieldsValid) {
        // Check if any field contains default placeholders
        const hasDefaultValues = requiredFields.some(field => {
          const value = extractedFields[field];
          if (!value) return true;
          const strValue = value.toString();
          return strValue.includes('<') && strValue.includes('>');
        });

        if (!hasDefaultValues) {
          setConfigurationExplicitlySaved(true);
          setSavedFieldValues(extractedFields);
        }
      }
    } catch (err) {
      setError('Failed to fetch rho.yaml: ' + (err.message || err));
    } finally {
      if (!silent) setLoading(false);
    }
    // eslint-disable-next-line
  }, [repo]);

  useEffect(() => {
    fetchYaml();
  }, [fetchYaml]);

  // Fetch customer details for the Customer Details tab
  useEffect(() => {
    async function fetchCustomerDetails() {
      setCustomerLoading(true);
      setCustomerError(null);
      try {
        const session = await getCurrentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await fetch(`${API_URL}/customer-details?repo=${encodeURIComponent(repo)}`, {
          method: 'GET',
          headers: { Authorization: idToken },
        });
        if (!response.ok) throw new Error(await response.text());
        const data = await response.json();
        setCustomerDetails(data);
        setCustomerForm({
          contact_name: data.contact_name || '',
          institution: data.institution || '',
          // Legacy records stored a single `address` string — fall back to it for
          // line 1 so existing data isn't lost when the form is split.
          address_line1: data.address_line1 || data.address || '',
          address_line2: data.address_line2 || '',
          address_line3: data.address_line3 || '',
          city: data.city || '',
          state_province: data.state_province || '',
          postal_code: data.postal_code || '',
          country: data.country || '',
          phone: data.phone || '',
          email: data.email || '',
          additional_details: data.additional_details || '',
        });
        setArchived(!!data.archived);
        setArchivedAt(data.archived_at || null);
        setArchivedBy(data.archived_by || null);
      } catch (err) {
        console.error('Failed to load customer details:', err);
        setCustomerError('Failed to load customer details: ' + (err.message || err));
      } finally {
        setCustomerLoading(false);
      }
    }
    fetchCustomerDetails();
    // eslint-disable-next-line
  }, [repo]);

  // Load the documentation listing for the selected version + jurisdiction.
  // Files are uploaded manually to the docs bucket; we link to whatever exists.
  useEffect(() => {
    const version = fields?.targetRevision;
    const jurisdiction = fields?.jurisdiction;
    if (!version) {
      setVersionDocs({ releaseNotes: [], manuals: [] });
      return;
    }
    let cancelled = false;
    (async () => {
      setVersionDocsLoading(true);
      try {
        const session = await getCurrentSession();
        const idToken = session.getIdToken().getJwtToken();
        const params = new URLSearchParams({ version });
        if (jurisdiction) params.set('jurisdiction', jurisdiction);
        const response = await fetch(`${API_URL}/version-docs?${params.toString()}`, {
          method: 'GET',
          headers: { Authorization: idToken },
        });
        if (!response.ok) throw new Error(await response.text());
        const data = await response.json();
        if (!cancelled) {
          setVersionDocs({ releaseNotes: data.releaseNotes || [], manuals: data.manuals || [] });
        }
      } catch (err) {
        console.error('Failed to load version documentation:', err);
        if (!cancelled) setVersionDocs({ releaseNotes: [], manuals: [] });
      } finally {
        if (!cancelled) setVersionDocsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [fields?.targetRevision, fields?.jurisdiction]);

  const validateCustomerField = (name, value) => {
    const empty = !value || !value.toString().trim();
    // Only required fields are flagged when empty; optional fields (e.g. address
    // lines 2/3, additional details) are valid when blank.
    if (empty) return customerRequiredFields.includes(name) ? 'This field is required' : '';
    if (name === 'email') {
      const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!re.test(value.trim())) return 'Enter a valid email address';
    }
    if (name === 'phone') {
      // Loose check: needs at least 7 digits, allow + ( ) - space
      if (!/[0-9]/.test(value) || value.replace(/[^0-9]/g, '').length < 7) return 'Enter a valid phone number';
    }
    return '';
  };

  const handleCustomerChange = (e) => {
    const { name, value } = e.target;
    setCustomerForm((prev) => ({ ...prev, [name]: value }));
    setCustomerValidationErrors((prev) => ({ ...prev, [name]: validateCustomerField(name, value) }));
    setCustomerSavedMsg(null);
  };

  useEffect(() => {
    async function fetchPostInstall() {
      setPostInstallLoading(true);
      setPostInstallError(null);
      try {
        const session = await getCurrentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await fetch(`${API_URL}/post-installation?repo=${encodeURIComponent(repo)}`, {
          method: 'GET',
          headers: { Authorization: idToken },
        });
        if (!response.ok) throw new Error(await response.text());
        const record = await response.json();
        setPostInstall(record);
        const defaults = buildPostInstallDefaults();
        const saved = (record && record.data) || {};
        setPostInstallForm({
          ...defaults,
          ...saved,
          configurables: {
            ...defaults.configurables,
            ...(saved.configurables || {}),
          },
        });
      } catch (err) {
        console.error('Failed to load post-installation record:', err);
        setPostInstallError('Failed to load post-installation record: ' + (err.message || err));
      } finally {
        setPostInstallLoading(false);
      }
    }
    fetchPostInstall();
    // eslint-disable-next-line
  }, [repo]);

  const handlePostInstallTopChange = (name, value) => {
    setPostInstallForm((prev) => ({ ...prev, [name]: value }));
    setPostInstallSavedMsg(null);
  };
  const handleConfigurableChange = (key, value) => {
    setPostInstallForm((prev) => ({
      ...prev,
      configurables: { ...prev.configurables, [key]: value },
    }));
    setPostInstallSavedMsg(null);
  };

  const handlePostInstallSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setPostInstallError(null);
    setPostInstallSavedMsg(null);
    setPostInstallSaving(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/post-installation?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: postInstallForm }),
      });
      if (!response.ok) throw new Error(await response.text());
      const updated = await response.json();
      setPostInstall(updated);
      setPostInstallSavedMsg('Post-installation record saved.');
    } catch (err) {
      setPostInstallError('Failed to save post-installation: ' + (err.message || err));
    } finally {
      setPostInstallSaving(false);
    }
  };

  // Load identity claims (email + name parts) from the Cognito ID token.
  // Cognito only exposes given_name/family_name in the JWT if the pool has those
  // attributes configured; if absent we leave the inputs blank for the user.
  useEffect(() => {
    async function loadIdentity() {
      try {
        const session = await getCurrentSession();
        const idToken = session.getIdToken().getJwtToken();
        const claims = decodeJWT(idToken) || {};
        const email = claims.email || '';
        let first = claims.given_name || '';
        let last = claims.family_name || '';
        // Fall back to splitting "name" if structured fields aren't present
        if (!first && !last && claims.name && typeof claims.name === 'string') {
          const parts = claims.name.trim().split(/\s+/);
          first = parts.shift() || '';
          last = parts.join(' ');
        }
        setCurrentUserEmail(email);
        setCurrentUserClaimNames({ first, last });
        setServiceForm((prev) => ({
          ...prev,
          first_name: prev.first_name || first,
          last_name: prev.last_name || last,
        }));
      } catch (err) {
        // Soft fail — leave identity blank
      }
    }
    loadIdentity();
  }, []);

  const fetchServiceLog = async () => {
    setServiceLogLoading(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/service-log?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken },
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setServiceLog(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      console.error('Failed to load service log:', err);
      setServiceLog([]);
    } finally {
      setServiceLogLoading(false);
    }
  };

  useEffect(() => {
    fetchServiceLog();
    // eslint-disable-next-line
  }, [repo]);

  const handleServiceFormChange = (e) => {
    const { name, value } = e.target;
    setServiceForm((prev) => ({ ...prev, [name]: value }));
    setServiceSavedMsg(null);
  };

  const handleServiceSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setServiceError(null);
    setServiceSavedMsg(null);
    if (!serviceForm.notes.trim()) {
      setServiceError('Service notes are required.');
      return;
    }
    setServiceSaving(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/service-log?repo=${encodeURIComponent(repo)}`, {
        method: 'POST',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          first_name: serviceForm.first_name.trim(),
          last_name: serviceForm.last_name.trim(),
          notes: serviceForm.notes,
        }),
      });
      if (!response.ok) throw new Error(await response.text());
      // Clear the notes field but keep the name pre-filled for subsequent entries.
      setServiceForm((prev) => ({ ...prev, notes: '' }));
      setServiceSavedMsg('Service log entry saved.');
      fetchServiceLog();
    } catch (err) {
      setServiceError('Failed to save service log entry: ' + (err.message || err));
    } finally {
      setServiceSaving(false);
    }
  };

  // Build the jurisdiction-aware training email body. Pulls jurisdiction from
  // the loaded rho.yaml fields so a US site gets the US-specific copy.
  const buildTrainingEmail = () => {
    const isUS = (fields?.jurisdiction || '').toUpperCase() === 'USA';
    const trainingVideo = isUS
      ? 'https://www.youtube.com/watch?v=y3FtjFOThSI'
      : 'https://www.youtube.com/watch?v=LHLTXrBBMHM';
    const docusign = 'https://powerforms.docusign.net/f0f14964-5592-4149-bb0e-97103e1233ca?env=ca&acct=2a417464-5c28-4daf-9f07-1194037e2057&accountId=2a417464-5c28-4daf-9f07-1194037e2057';
    const subject = 'Rho Training — required action';
    const body = `Hi,

At least one IT Administrator is required to review this training video for Rho and attest to completing the training using the form below. You are welcome to share the training slides with other Rho Users in your imaging clinic. The video and sign-off can be completed in under 10 minutes.

Training video (${isUS ? 'US' : 'Non-US'}): ${trainingVideo}
Attestation form: ${docusign}

Thank you,
16 Bit`;
    return { isUS, subject, body, trainingVideo, docusign };
  };

  const fetchConfigAudit = async () => {
    setConfigAuditLoading(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/rho-configuration/audit?repo=${encodeURIComponent(repo)}`, {
        method: 'GET',
        headers: { Authorization: idToken },
      });
      if (!response.ok) throw new Error(await response.text());
      const data = await response.json();
      setConfigAudit(Array.isArray(data.entries) ? data.entries : []);
    } catch (err) {
      console.error('Failed to load configuration audit:', err);
      // Soft-fail: just leave empty
      setConfigAudit([]);
    } finally {
      setConfigAuditLoading(false);
    }
  };

  useEffect(() => {
    fetchConfigAudit();
    // eslint-disable-next-line
  }, [repo]);

  const handleCustomerSave = async (e) => {
    if (e && e.preventDefault) e.preventDefault();
    setCustomerError(null);
    setCustomerSavedMsg(null);

    // Validate all fields up front
    const errs = {};
    customerRequiredFields.forEach((k) => {
      const msg = validateCustomerField(k, customerForm[k]);
      if (msg) errs[k] = msg;
    });
    setCustomerValidationErrors(errs);
    if (Object.keys(errs).length > 0) {
      setCustomerError('Please complete all required fields with valid values.');
      return;
    }

    setCustomerSaving(true);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/customer-details?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify(customerForm),
      });
      if (!response.ok) throw new Error(await response.text());
      const updated = await response.json();
      setCustomerDetails(updated);
      setCustomerSavedMsg('Customer details saved.');
    } catch (err) {
      setCustomerError('Failed to save customer details: ' + (err.message || err));
    } finally {
      setCustomerSaving(false);
    }
  };

  // Renders one customer-details text input. Keeps the verbose styling/validation
  // markup in one place now that the address is split into several fields.
  const customerField = (name, label, { required = false, placeholder = '', type = 'text', fullWidth = false, options = null } = {}) => (
    <div style={fullWidth ? { ...formStyles.field, gridColumn: '1 / -1' } : formStyles.field}>
      <label style={formStyles.label}>
        {label} {required && <span style={{ color: '#e74c3c' }}>*</span>}
      </label>
      {options ? (
        <select
          name={name}
          value={customerForm[name]}
          onChange={handleCustomerChange}
          disabled={archived}
          style={{
            ...formStyles.select,
            borderColor: customerValidationErrors[name] ? '#e74c3c' : '#e1e5e9',
            ...(archived ? { background: '#f1f3f5', cursor: 'not-allowed' } : {}),
          }}
          required={required}
        >
          <option value="">{placeholder || `Select ${label.toLowerCase()}`}</option>
          {/* Preserve any legacy free-text value not present in the option list. */}
          {customerForm[name] && !options.includes(customerForm[name]) && (
            <option value={customerForm[name]}>{customerForm[name]}</option>
          )}
          {options.map((opt) => <option key={opt} value={opt}>{opt}</option>)}
        </select>
      ) : (
        <input
          type={type}
          name={name}
          value={customerForm[name]}
          onChange={handleCustomerChange}
          placeholder={placeholder}
          disabled={archived}
          style={{
            ...formStyles.input,
            borderColor: customerValidationErrors[name] ? '#e74c3c' : '#e1e5e9',
            ...(archived ? { background: '#f1f3f5', cursor: 'not-allowed' } : {}),
          }}
          required={required}
        />
      )}
      {customerValidationErrors[name] && (
        <div style={{ color: '#e74c3c', fontSize: '12px', marginTop: '4px', fontWeight: 500 }}>
          {customerValidationErrors[name]}
        </div>
      )}
    </div>
  );

  // Compose a single-line address for display. Handles both the new structured
  // fields and legacy records that only had a single `address` string.
  const formatCustomerAddress = (rec) => {
    if (!rec) return '';
    const parts = [
      rec.address_line1, rec.address_line2, rec.address_line3,
      rec.city, rec.state_province, rec.postal_code, rec.country,
    ].filter((p) => p && p.toString().trim());
    if (parts.length > 0) return parts.join(', ');
    return rec.address || '';
  };

  const handleArchive = async () => {
    setArchiving(true);
    setArchiveError(null);
    try {
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/archive-repo?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: { Authorization: idToken, 'Content-Type': 'application/json' },
        body: JSON.stringify({ confirm: true }),
      });
      if (!response.ok) throw new Error(await response.text());
      const updated = await response.json();
      setArchived(true);
      setArchivedAt(updated.archived_at || new Date().toISOString());
      setArchivedBy(updated.archived_by || null);
      setShowArchiveModal(false);
    } catch (err) {
      setArchiveError('Failed to archive repository: ' + (err.message || err));
    } finally {
      setArchiving(false);
    }
  };

  // Fetch GitHub releases for targetRevision dropdown
  useEffect(() => {
    async function fetchReleases() {
      setReleasesLoading(true);
      try {
        const session = await getCurrentSession();
        const idToken = session.getIdToken().getJwtToken();
        const response = await fetch(`${API_URL}/releases`, {
          headers: { Authorization: idToken }
        });
        if (!response.ok) throw new Error('Failed to fetch releases');
        const data = await response.json();
        const tags = data.tags || [];
        // Hardcode v3.1.0-alpha since there's no actual release for it yet
        const tagsWithAlpha = [...tags];
        if (!tagsWithAlpha.includes('v3.1.0-alpha')) {
          tagsWithAlpha.push('v3.1.0-alpha');
        }
        setReleases(tagsWithAlpha);

        // Find the latest release (highest version)
        if (tagsWithAlpha.length > 0) {
          const sortedTags = tagsWithAlpha
            .filter(tag => tag.match(/^v\d+\.\d+\.\d+$/) || tag === 'v3.1.0-alpha')
            .sort((a, b) => {
              const aVersion = a.substring(1).split('.').map(Number);
              const bVersion = b.substring(1).split('.').map(Number);
              for (let i = 0; i < 3; i++) {
                if (aVersion[i] !== bVersion[i]) {
                  return bVersion[i] - aVersion[i];
                }
              }
              return 0;
            });
          if (sortedTags.length > 0) {
            const latestStableRelease = sortedTags[0];
            setLatestRelease(latestStableRelease);

            // Set default targetRevision to latest stable release only if not already set
            if (fields && !fields.targetRevision) {
              setFields(prev => ({ ...prev, targetRevision: latestStableRelease }));
              // Update savedFieldValues if this is an automatic default setting
              if (savedFieldValues && !savedFieldValues.targetRevision) {
                setSavedFieldValues(prev => ({ ...prev, targetRevision: latestStableRelease }));
              }
            }
          }
        }
      } catch (err) {
        console.error('Error fetching releases:', err);
        setReleases([]);
      } finally {
        setReleasesLoading(false);
      }
    }

    // Only fetch releases if we have fields loaded
    if (fields) {
      fetchReleases();
    }
  }, [fields]);

  // Fetch deploy keys on mount
  useEffect(() => {
    fetchDeployKeys();
  }, [repo]);

  // Set default targetRevision to latest stable release when both are available
  useEffect(() => {
    if (fields && latestRelease && !fields.targetRevision) {
      setFields(prev => ({ ...prev, targetRevision: latestRelease }));
      // Update savedFieldValues if this is an automatic default setting
      if (savedFieldValues && !savedFieldValues.targetRevision) {
        setSavedFieldValues(prev => ({ ...prev, targetRevision: latestRelease }));
      }
    }
  }, [fields, latestRelease, savedFieldValues]);

  // Helper: Check if current fields match saved values
  const fieldsMatchSaved = () => {
    if (!savedFieldValues || !fields) return false;

    const requiredFields = ['targetRevision', 'name', 'jurisdiction', 'tz', 'stage', 'device', 'host'];
    return requiredFields.every(field => {
      const currentValue = fields[field];
      const savedValue = savedFieldValues[field];

      // Handle undefined/null cases
      if (!currentValue && !savedValue) return true;
      if (!currentValue || !savedValue) return false;

      return currentValue.toString() === savedValue.toString();
    });
  };

  // Validation functions for each field type
  const validateField = (fieldName, value) => {
    switch (fieldName) {
      case 'host':
        if (!value || value.trim().length === 0) return 'Host is required';
        // Check for default values
        if (value === '<machine-ip>' || value === '<cluster-name>' || value === '<host>') return 'Please enter a valid IP address';
        // Validate IP format
        const ipRegex = /^(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)\.(25[0-5]|2[0-4][0-9]|[01]?[0-9][0-9]?)$/;
        if (!ipRegex.test(value)) return 'Please enter a valid IP address (e.g., 192.168.1.45)';
        return '';

      case 'name':
        if (!value || value.trim().length === 0) return 'Name is required';
        if (value === '<cluster-name>' || value === '<name>') return 'Please enter a valid cluster name';
        if (value.length < 2) return 'Name must be at least 2 characters long';
        if (value.length > 50) return 'Name must be 50 characters or less';
        return '';

      case 'jurisdiction':
        if (!value || value.trim().length === 0) return 'Jurisdiction is required';
        if (value === '<jurisdiction>' || value === '<jurisdiction-type>') return 'Please enter a valid jurisdiction';
        return '';

      case 'targetRevision':
        if (!value || value.trim().length === 0) return 'Target revision is required';
        if (value === '<target-revision>' || value === '<version>') return 'Please enter a valid target revision';
        return '';

      case 'tz':
        if (!value || value.trim().length === 0) return 'Timezone is required';
        if (value === '<timezone>' || value === '<tz>') return 'Please enter a valid timezone';
        return '';

      case 'stage':
        if (!value || value.trim().length === 0) return 'Stage is required';
        if (value === '<stage>' || value === '<environment>') return 'Please enter a valid stage';
        return '';

      case 'device':
        if (!value || value.trim().length === 0) return 'Device is required';
        if (value === '<device>' || value === '<device-type>') return 'Please enter a valid device type';
        return '';

      case 'dicomAet':
        if (!value || value.trim().length === 0) return 'DICOM AET is required';
        if (value.length > 16) return 'DICOM AET cannot be longer than 16 characters';
        if (!/^[A-Za-z0-9_-]+$/.test(value)) return 'DICOM AET can only contain letters, numbers, hyphens, and underscores';
        return '';

      default:
        return '';
    }
  };

  // Check if configuration is complete and saved
  useEffect(() => {
    if (fields && Object.keys(fields).length > 0) {
      // Check if ALL required fields are filled in
      const requiredFields = [
        'targetRevision',
        'name',
        'jurisdiction',
        'tz',
        'stage',
        'device',
        'host'
      ];

      // Use same required fields for both admins and distributors
      const allRequiredFields = requiredFields;

      // Validate all required fields and check for default values
      const validationResults = {};
      let allFieldsValid = true;

      allRequiredFields.forEach(field => {
        const value = fields[field];
        const error = validateField(field, value);
        validationResults[field] = error;
        if (error) allFieldsValid = false;
      });

      // Update validation errors state
      setValidationErrors(validationResults);

      // Configuration is considered "saved" if:
      // 1. All fields are valid AND
      // 2. Either explicitly saved OR current fields match previously saved values
      const fieldsAreSaved = configurationExplicitlySaved || fieldsMatchSaved();
      setConfigurationSaved(allFieldsValid && fieldsAreSaved);

      // Log validation status for debugging
      if (!allFieldsValid) {
        console.log('Configuration validation failed:', validationResults);
      }
      if (allFieldsValid && !fieldsAreSaved) {
        console.log('Fields are valid but not saved or changed from saved values');
      }
    }
  }, [fields, configurationExplicitlySaved, savedFieldValues]);

  // Handle form field change
  function handleChange(e) {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;

    setFields({ ...fields, [name]: newValue });

    // Only reset explicit save state if the field actually changed from its saved value
    if (configurationExplicitlySaved && savedFieldValues && savedFieldValues[name] !== newValue) {
      setConfigurationExplicitlySaved(false);
    }
  }

  // Handle save
  async function handleSave(e) {
    e.preventDefault();

    // Check for validation errors
    const hasValidationErrors = Object.values(validationErrors).some(error => error && error.length > 0);
    if (hasValidationErrors) {
      setError('Please fix validation errors before saving');
      return;
    }

    setSaving(true);
    setError(null);
    try {
      let doc = yaml.load(rawYaml);
      doc = updateYamlObj(doc, fields);
      const newYaml = yaml.dump(doc, {
        quotingType: '"',
        forceQuotes: false
      });
      const session = await getCurrentSession();
      const idToken = session.getIdToken().getJwtToken();
      const response = await fetch(`${API_URL}/rho-configuration?repo=${encodeURIComponent(repo)}`, {
        method: 'PUT',
        headers: {
          Authorization: idToken,
          'Content-Type': 'text/yaml',
        },
        body: newYaml,
      });
      if (!response.ok) throw new Error(await response.text());
      setRawYaml(newYaml);
      setConfigurationExplicitlySaved(true);
      // Store the current field values as the saved state
      setSavedFieldValues({ ...fields });
      // Refresh the audit log so the new entry appears immediately
      fetchConfigAudit();
      alert(`Rho configuration updated successfully for ${repo}`);
    } catch (err) {
      setError('Failed to save: ' + (err.message || err));
    } finally {
      setSaving(false);
    }
  }

  const formStyles = {
    container: {
      maxWidth: '800px',
      margin: '0 auto',
      padding: '20px',
      fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    },
    header: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: '2px solid #e1e5e9',
    },
    headerLeft: {
      display: 'flex',
      alignItems: 'center',
      gap: '20px',
    },
    title: {
      color: '#2c3e50',
      margin: 0,
      fontSize: '28px',
      fontWeight: '600',
    },
    repoName: {
      color: '#3498db',
      fontWeight: '500',
    },
    backButton: {
      background: '#95a5a6',
      color: 'white',
      border: 'none',
      borderRadius: '6px',
      padding: '8px 16px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '13px',
      transition: 'background 0.2s',
      whiteSpace: 'nowrap',
    },
    signOutButton: {
      background: '#e74c3c',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '10px 20px',
      cursor: 'pointer',
      fontWeight: '500',
      fontSize: '14px',
      transition: 'background 0.2s',
    },
    form: {
      background: 'white',
      borderRadius: '12px',
      padding: '30px',
      boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
      border: '1px solid #e1e5e9',
    },
    section: {
      marginBottom: '30px',
      paddingBottom: '20px',
      borderBottom: '1px solid #ecf0f1',
    },
    sectionTitle: {
      color: '#2c3e50',
      fontSize: '20px',
      fontWeight: '600',
      marginBottom: '20px',
      paddingBottom: '10px',
      borderBottom: '2px solid #3498db',
    },
    fieldGroup: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
      gap: '20px',
      marginBottom: '20px',
    },
    field: {
      display: 'flex',
      flexDirection: 'column',
    },
    label: {
      marginBottom: '8px',
      fontWeight: '500',
      color: '#34495e',
      fontSize: '14px',
    },
    input: {
      padding: '12px',
      border: '2px solid #e1e5e9',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'border-color 0.2s',
      backgroundColor: '#f8f9fa',
    },
    select: {
      padding: '12px',
      border: '2px solid #e1e5e9',
      borderRadius: '8px',
      fontSize: '14px',
      transition: 'border-color 0.2s',
      backgroundColor: '#f8f9fa',
    },
    docLink: {
      display: 'flex',
      alignItems: 'center',
      gap: '5px',
      width: 'fit-content',
      marginTop: '8px',
      color: '#3498db',
      fontSize: '13px',
      fontWeight: 500,
      textDecoration: 'none',
    },
    docMuted: {
      fontSize: '12px',
      color: '#999',
      marginTop: '8px',
    },
    checkboxGroup: {
      display: 'grid',
      gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
      gap: '15px',
      marginTop: '10px',
    },
    checkboxField: {
      display: 'flex',
      alignItems: 'center',
      gap: '10px',
    },
    checkbox: {
      width: '18px',
      height: '18px',
      accentColor: '#3498db',
    },
    checkboxLabel: {
      fontSize: '14px',
      color: '#34495e',
      cursor: 'pointer',
    },
    saveButton: {
      background: '#27ae60',
      color: 'white',
      border: 'none',
      borderRadius: '8px',
      padding: '15px 30px',
      cursor: 'pointer',
      fontWeight: '600',
      fontSize: '16px',
      transition: 'background 0.2s',
      width: '100%',
      marginTop: '20px',
    },
    disabledButton: {
      background: '#bdc3c7',
      cursor: 'not-allowed',
    },
    error: {
      color: '#e74c3c',
      background: '#fdf2f2',
      padding: '15px',
      borderRadius: '8px',
      marginBottom: '20px',
      border: '1px solid #fecaca',
    },
    loading: {
      textAlign: 'center',
      padding: '40px',
      color: '#7f8c8d',
      fontSize: '16px',
    },
    adminBadge: {
      background: '#e74c3c',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      marginLeft: '10px',
    },
    userBadge: {
      background: '#3498db',
      color: 'white',
      padding: '4px 12px',
      borderRadius: '20px',
      fontSize: '12px',
      fontWeight: '500',
      marginLeft: '10px',
    },
    debugInfo: {
      background: '#f8f9fa',
      padding: '10px',
      borderRadius: '6px',
      fontSize: '12px',
      color: '#6c757d',
      marginBottom: '20px',
      border: '1px solid #dee2e6',
    },
  };

  return (
    <div style={formStyles.container}>
      <div style={formStyles.header}>
        <div style={formStyles.headerLeft}>
          <button onClick={() => navigate(-1)} style={formStyles.backButton}>
            ← Back
          </button>
          <h1 style={formStyles.title}>
            Configuration for
            <span style={formStyles.repoName}> {repo}</span>
            <span style={isAdmin ? formStyles.adminBadge : formStyles.userBadge}>
              {isAdmin ? 'Admin' : 'User'}
            </span>
          </h1>
        </div>
        <button onClick={handleSignOut} style={formStyles.signOutButton}>
          Sign Out
        </button>
      </div>

      {loading && <div style={formStyles.loading}>Loading configuration...</div>}
      {error && <div style={formStyles.error}>{error}</div>}

      {fields && !loading && (
        <>
          {/* Show save status indicator */}
          {!configurationSaved && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              color: '#856404',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div>
                <strong>Configuration incomplete or not saved</strong>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  {!configurationExplicitlySaved
                    ? 'Please fill in all required fields with valid values (no default placeholders) and save your configuration to access license management features.'
                    : 'Please fill in all required fields with valid values (no default placeholders) to access license management features.'
                  }
                  {Object.keys(validationErrors).some(key => validationErrors[key]) && (
                    <div style={{ marginTop: '8px', color: '#e74c3c' }}>
                      <strong>Validation errors found:</strong> Please fix the highlighted fields above.
                    </div>
                  )}
                  {configurationExplicitlySaved && fieldsMatchSaved() && (
                    <div style={{ marginTop: '8px', color: '#27ae60' }}>
                      <strong>Configuration matches saved values:</strong> All fields are valid and match your previously saved configuration.
                    </div>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Debug Information - Remove in production */}
          {process.env.NODE_ENV === 'development' && (
            <div style={formStyles.debugInfo}>
              <strong>Debug Info:</strong>
              <div>Configuration Saved: {configurationSaved ? 'Yes' : 'No'}</div>
              <div>Explicitly Saved: {configurationExplicitlySaved ? 'Yes' : 'No'}</div>
              <div>Fields Match Saved: {fieldsMatchSaved() ? 'Yes' : 'No'}</div>
              <div>Saved Field Values: {savedFieldValues ? 'Set' : 'Not Set'}</div>
            </div>
          )}

          {/* Customer details required warning */}
          {!customerLoading && !customerDetailsComplete && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              color: '#856404',
              padding: '12px 16px',
              borderRadius: '8px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '18px' }}>⚠️</span>
              <div>
                <strong>Customer details required before installation</strong>
                <div style={{ fontSize: '14px', marginTop: '4px' }}>
                  Complete the Customer Details tab — contact name, institution, address, country, phone, and email are mandatory prior to installation.
                </div>
              </div>
              <button
                type="button"
                onClick={() => setActiveTab('customer')}
                style={{
                  marginLeft: 'auto',
                  background: '#3498db',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  padding: '8px 14px',
                  cursor: 'pointer',
                  fontWeight: 600,
                  fontSize: '13px',
                }}
              >
                Open Customer Details
              </button>
            </div>
          )}

          {/* Installation Command Display — gated on customer details being complete */}
          {!ghcrKeyLoading && ghcrKey && customerDetailsComplete && (
            <div style={{
              background: '#f8f9fa',
              border: '2px solid #3498db',
              borderRadius: '12px',
              padding: '20px',
              marginBottom: '20px'
            }}>
              <h3 style={{
                color: '#2c3e50',
                fontSize: '18px',
                fontWeight: '600',
                marginBottom: '12px',
                display: 'flex',
                alignItems: 'center',
                gap: '10px'
              }}>
                <span style={{ fontSize: '24px' }}>📦</span>
                Installation Command
              </h3>
              <div style={{
                background: '#2c3e50',
                borderRadius: '8px',
                padding: '16px',
                marginBottom: '12px',
                position: 'relative'
              }}>
                <pre style={{
                  color: '#ecf0f1',
                  fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                  fontSize: '13px',
                  margin: 0,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-all',
                  lineHeight: '1.6'
                }}>
                  {`RHO_CUSTOMER_NAME=${repo && repo.startsWith('rho-customer-') ? repo.replace('rho-customer-', '') : repo} \\
RHO_GHCR_KEY=${ghcrKey} \\
bash <(curl -fsSL https://raw.githubusercontent.com/16-Bit-Inc/gists/main/install-rho.sh)`}
                </pre>
                <button
                  onClick={() => {
                    const command = `RHO_CUSTOMER_NAME=${repo && repo.startsWith('rho-customer-') ? repo.replace('rho-customer-', '') : repo} \\
RHO_GHCR_KEY=${ghcrKey} \\
bash <(curl -fsSL https://raw.githubusercontent.com/16-Bit-Inc/gists/main/install-rho.sh)`;
                    navigator.clipboard.writeText(command);
                    alert('Installation command copied to clipboard!');
                  }}
                  style={{
                    position: 'absolute',
                    top: '12px',
                    right: '12px',
                    background: '#3498db',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    padding: '8px 16px',
                    cursor: 'pointer',
                    fontSize: '12px',
                    fontWeight: '500',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = '#2980b9'}
                  onMouseLeave={(e) => e.target.style.background = '#3498db'}
                >
                  📋 Copy
                </button>
              </div>
              <div style={{
                fontSize: '13px',
                color: '#7f8c8d',
                lineHeight: '1.5'
              }}>
                <strong>Note:</strong> Run this command on your target machine to install Rho. The customer name and GHCR key are automatically configured for your organization.
              </div>
            </div>
          )}

          {!ghcrKeyLoading && !ghcrKey && (
            <div style={{
              background: '#fff3cd',
              border: '1px solid #ffeaa7',
              borderRadius: '12px',
              padding: '16px',
              marginBottom: '20px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <span style={{ fontSize: '24px' }}>⚠️</span>
              <div style={{ flex: 1 }}>
                <strong style={{ color: '#856404', display: 'block', marginBottom: '4px' }}>
                  No GHCR Key Available
                </strong>
                <div style={{ fontSize: '14px', color: '#856404' }}>
                  Your organization does not have a GHCR key set. Please contact your 16bit administrator to set up a GHCR key for installation.
                </div>
              </div>
            </div>
          )}

          {/* Tab bar: Configuration / Customer Details */}
          <div style={{
            display: 'flex',
            gap: '4px',
            borderBottom: '1px solid #e1e5e9',
            marginBottom: '20px',
          }}>
            {[
              { key: 'customer', label: 'Customer Details', warn: !customerLoading && !customerDetailsComplete },
              { key: 'config', label: 'Configuration' },
              { key: 'postinstall', label: 'Post-Installation' },
              { key: 'service', label: 'Service' },
              ...(isAdmin ? [{ key: 'readme', label: 'README' }, { key: 'rawyaml', label: 'rho.yaml' }] : []),
            ].map((tab) => {
              const isActive = activeTab === tab.key;
              return (
                <button
                  key={tab.key}
                  type="button"
                  onClick={() => setActiveTab(tab.key)}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    borderBottom: isActive ? '3px solid #3498db' : '3px solid transparent',
                    padding: '12px 18px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: isActive ? 700 : 500,
                    color: isActive ? '#2c3e50' : '#7f8c8d',
                    transition: 'border-color 0.15s, color 0.15s',
                    marginBottom: '-1px',
                  }}
                >
                  {tab.label}
                  {tab.warn && (
                    <span title="Required fields missing" style={{ marginLeft: 8, color: '#e67e22' }}>●</span>
                  )}
                </button>
              );
            })}
          </div>

          {archived && (
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '12px',
              background: '#f8d7da',
              border: '1px solid #f5c6cb',
              color: '#721c24',
              borderRadius: '10px',
              padding: '14px 18px',
              marginBottom: '20px',
              fontSize: '14px',
            }}>
              <span style={{ fontSize: '20px' }}>🗄️</span>
              <div>
                <strong>This repository is archived.</strong> It is read-only — no further changes can be made.
                {archivedAt ? <> Archived by {archivedBy || 'unknown'} on {formatDateTime(archivedAt)}.</> : null}
              </div>
            </div>
          )}

          {activeTab === 'config' && (
          <form onSubmit={handleSave} style={formStyles.form}>
            {/* Basic Configuration */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>Basic Configuration</h2>
              <div style={formStyles.fieldGroup}>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Target Revision</label>
                  <div style={{ fontSize: '12px', color: '#666', marginBottom: '8px' }}>
                    Defaults to latest stable version if not set (e.g., v3.0.1, v3.0.2)
                  </div>
                  {releasesLoading ? (
                    <input
                      name="targetRevision"
                      value={fields.targetRevision}
                      onChange={handleChange}
                      style={formStyles.input}
                      placeholder="Loading releases..."
                      disabled
                    />
                  ) : isAdmin ? (
                    releases.length > 0 ? (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                        <select
                          name="targetRevision"
                          value={fields.targetRevision}
                          onChange={handleChange}
                          style={{
                            ...formStyles.select,
                            borderColor: validationErrors.targetRevision ? '#e74c3c' : '#e1e5e9'
                          }}
                        >
                          <option value="">Select a release</option>
                          {releases.map(tag => (
                            <option key={tag} value={tag}>{tag}</option>
                          ))}
                        </select>
                        {latestRelease && fields.targetRevision && fields.targetRevision !== latestRelease && (
                          <button
                            type="button"
                            onClick={() => handleChange({ target: { name: 'targetRevision', value: latestRelease } })}
                            style={{
                              background: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontSize: '12px',
                              fontWeight: '500'
                            }}
                          >
                            Update to latest ({latestRelease})
                          </button>
                        )}
                      </div>
                    ) : (
                      <input
                        name="targetRevision"
                        value={fields.targetRevision}
                        onChange={handleChange}
                        style={{
                          ...formStyles.input,
                          borderColor: validationErrors.targetRevision ? '#e74c3c' : '#e1e5e9'
                        }}
                        placeholder="e.g., v2.7.3"
                      />
                    )
                  ) : (
                    // Distributor: may choose any of the most recent stable releases
                    // (plus whatever is currently deployed), and jump to latest.
                    (() => {
                      const recent = sortStableReleases(releases).slice(0, DISTRIBUTOR_RELEASE_CHOICES);
                      const current = fields.targetRevision || '';
                      const options = current && !recent.includes(current) ? [current, ...recent] : recent;
                      return (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      <div style={{ fontSize: '12px', color: '#666', marginBottom: '4px' }}>
                        Choose one of the {DISTRIBUTOR_RELEASE_CHOICES} most recent stable releases
                        {current && !recent.includes(current) ? ` (currently on ${current})` : ''}
                      </div>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
                        <select
                          name="targetRevision"
                          value={current}
                          onChange={handleChange}
                          style={{
                            ...formStyles.select,
                            flex: '1',
                            minWidth: '140px',
                            borderColor: validationErrors.targetRevision ? '#e74c3c' : '#e1e5e9'
                          }}
                        >
                          {!current && <option value="">Select a release</option>}
                          {options.map((tag) => (
                            <option key={tag} value={tag}>
                              {tag}{tag === latestRelease ? ' (latest)' : ''}{tag === current && !recent.includes(tag) ? ' (current)' : ''}
                            </option>
                          ))}
                        </select>
                        {latestRelease && (!fields.targetRevision || fields.targetRevision !== latestRelease) && (
                          <button
                            type="button"
                            onClick={() => handleChange({ target: { name: 'targetRevision', value: latestRelease } })}
                            style={{
                              background: '#3498db',
                              color: 'white',
                              border: 'none',
                              borderRadius: '6px',
                              padding: '8px 16px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              fontWeight: '600',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            Update to latest revision ({latestRelease})
                          </button>
                        )}
                      </div>
                    </div>
                      );
                    })()
                  )}
                  {validationErrors.targetRevision && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.targetRevision}
                    </div>
                  )}
                  {/* Release notes for the selected version (shared across all jurisdictions). */}
                  {fields.targetRevision && !versionDocsLoading && versionDocs.releaseNotes.map((doc) => (
                    <a
                      key={doc.key}
                      href={`/${doc.key.split('/').map(encodeURIComponent).join('/')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={formStyles.docLink}
                      title={doc.filename}
                    >
                      <span style={{ textDecoration: 'underline' }}>Release notes for {fields.targetRevision}</span>
                      <ExternalLinkIcon />
                    </a>
                  ))}
                  {fields.targetRevision && !versionDocsLoading && versionDocs.releaseNotes.length === 0 && (
                    <div style={formStyles.docMuted}>Release notes not yet available for {fields.targetRevision}.</div>
                  )}
                </div>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Name</label>
                  <input
                    name="name"
                    value={fields.name}
                    onChange={handleChange}
                    style={formStyles.input}
                    placeholder="e.g., testing"
                    disabled
                    title="This field is set automatically from the repository name and cannot be changed."
                  />
                  {validationErrors.name && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.name}
                    </div>
                  )}
                </div>
              </div>
              <div style={formStyles.fieldGroup}>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Jurisdiction</label>
                  <select
                    name="jurisdiction"
                    value={fields.jurisdiction}
                    onChange={handleChange}
                    style={{
                      ...formStyles.select,
                      borderColor: validationErrors.jurisdiction ? '#e74c3c' : '#e1e5e9'
                    }}
                  >
                    <option value="">Select jurisdiction</option>
                    <option value="USA">USA</option>
                    <option value="CAN">CAN</option>
                    <option value="AUS">AUS</option>
                    <option value="SGP">SGP</option>
                    <option value="EUR">EUR</option>
                  </select>
                  {validationErrors.jurisdiction && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.jurisdiction}
                    </div>
                  )}
                  {/* User guide specific to the selected jurisdiction + version. */}
                  {fields.targetRevision && fields.jurisdiction && !versionDocsLoading && versionDocs.manuals.map((doc) => (
                    <a
                      key={doc.key}
                      href={`/${doc.key.split('/').map(encodeURIComponent).join('/')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={formStyles.docLink}
                      title={doc.filename}
                    >
                      <span style={{ textDecoration: 'underline' }}>User guide for {fields.jurisdiction}</span>
                      <ExternalLinkIcon />
                    </a>
                  ))}
                  {fields.targetRevision && fields.jurisdiction && !versionDocsLoading && versionDocs.manuals.length === 0 && (
                    <div style={formStyles.docMuted}>User guide not yet available for {fields.jurisdiction} ({fields.targetRevision}).</div>
                  )}
                </div>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Use Case</label>
                  <select
                    name="useCase"
                    value={fields.useCase}
                    onChange={handleChange}
                    style={formStyles.select}
                  >
                    <option value="">Select use case</option>
                    <option value="Clinical">Clinical</option>
                    <option value="Research">Research</option>
                    <option value="Demo">Demo</option>
                  </select>
                </div>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Timezone</label>
                  <select
                    name="tz"
                    value={fields.tz}
                    onChange={handleChange}
                    style={{
                      ...formStyles.select,
                      borderColor: validationErrors.tz ? '#e74c3c' : '#e1e5e9'
                    }}
                  >
                    {timezones.map(tz => (
                      <option key={tz} value={tz}>
                        {tz.replace(/_/g, ' ')} {tz === Intl.DateTimeFormat().resolvedOptions().timeZone ? '(Current)' : ''}
                      </option>
                    ))}
                  </select>
                  {validationErrors.tz && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.tz}
                    </div>
                  )}
                </div>
              </div>
              <div style={formStyles.fieldGroup}>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Stage</label>
                  {isAdmin ? (
                    <select
                      name="stage"
                      value={fields.stage}
                      onChange={handleChange}
                      style={{
                        ...formStyles.select,
                        borderColor: validationErrors.stage ? '#e74c3c' : '#e1e5e9'
                      }}
                    >
                      <option value="">Select stage</option>
                      <option value="test">test</option>
                      <option value="semi-online">semi-online</option>
                      <option value="prod">prod</option>
                    </select>
                  ) : (
                    <select
                      name="stage"
                      value={fields.stage}
                      onChange={handleChange}
                      style={{
                        ...formStyles.select,
                        borderColor: validationErrors.stage ? '#e74c3c' : '#e1e5e9'
                      }}
                    >
                      <option value="">Select stage</option>
                      <option value="semi-online">semi-online</option>
                      <option value="prod">prod</option>
                    </select>
                  )}
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    <strong>test:</strong> Development/testing environment<br />
                    <strong>semi-online:</strong> Internet connection only during install<br />
                    <strong>prod:</strong> Production environment
                  </div>
                  {validationErrors.stage && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.stage}
                    </div>
                  )}
                </div>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Device</label>
                  {isAdmin ? (
                    <select
                      name="device"
                      value={fields.device}
                      onChange={handleChange}
                      style={{
                        ...formStyles.select,
                        borderColor: validationErrors.device ? '#e74c3c' : '#e1e5e9'
                      }}
                    >
                      <option value="">Select device</option>
                      <option value="cpu">cpu</option>
                      <option value="gpu">gpu</option>
                    </select>
                  ) : (
                    <select
                      name="device"
                      value={fields.device}
                      onChange={handleChange}
                      style={{
                        ...formStyles.select,
                        borderColor: validationErrors.device ? '#e74c3c' : '#e1e5e9'
                      }}
                    >
                      <option value="">Select device</option>
                      <option value="cpu">cpu</option>
                    </select>
                  )}
                  {validationErrors.device && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.device}
                    </div>
                  )}
                </div>
              </div>
              <div style={formStyles.fieldGroup}>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Host</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <input
                      name="host"
                      value={fields.host}
                      onChange={handleChange}
                      style={{
                        ...formStyles.input,
                        borderColor: validationErrors.host ? '#e74c3c' : '#e1e5e9',
                        flex: 1
                      }}
                      placeholder="e.g., 192.168.1.45"
                    />
                    <div style={{
                      color: '#95a5a6',
                      fontSize: '14px',
                      fontWeight: '500',
                      userSelect: 'none',
                      padding: '12px 8px'
                    }}>
                      .nip.io
                    </div>
                  </div>
                  {validationErrors.host && (
                    <div style={{
                      color: '#e74c3c',
                      fontSize: '12px',
                      marginTop: '4px',
                      fontWeight: '500'
                    }}>
                      {validationErrors.host}
                    </div>
                  )}
                </div>
                {isAdmin && (
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>License Key</label>
                    <input
                      name="license"
                      value={fields.license}
                      onChange={handleChange}
                      style={formStyles.input}
                      placeholder="License key (JWT token)"
                    />
                  </div>
                )}
                {isAdmin && (
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>DICOM AET</label>
                    <input
                      name="dicomAet"
                      value={fields.dicomAet}
                      onChange={handleChange}
                      style={{
                        ...formStyles.input,
                        borderColor: validationErrors.dicomAet ? '#e74c3c' : '#e1e5e9'
                      }}
                      placeholder="e.g., rhopacs"
                      maxLength={16}
                    />
                    {validationErrors.dicomAet && (
                      <div style={{
                        color: '#e74c3c',
                        fontSize: '12px',
                        marginTop: '4px',
                        fontWeight: '500'
                      }}>
                        {validationErrors.dicomAet}
                      </div>
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Monitoring Configuration - Grafana Credentials */}
            <div style={formStyles.section}>
              <h2 style={formStyles.sectionTitle}>Monitoring Configuration</h2>
              <div style={{ ...formStyles.fieldGroup, gridTemplateColumns: '1fr 1fr' }}>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Grafana Username</label>
                  <input
                    name="grafanaUsername"
                    value={fields.grafanaUsername}
                    onChange={handleChange}
                    style={{
                      ...formStyles.input,
                      backgroundColor: '#e9ecef',
                      cursor: 'not-allowed',
                      color: '#6c757d'
                    }}
                    disabled
                    title="Grafana username is fixed and cannot be changed"
                  />
                  <div style={{ fontSize: '12px', color: '#6c757d', marginTop: '4px' }}>
                    Username is fixed as 'admin'
                  </div>
                </div>
                <div style={formStyles.field}>
                  <label style={formStyles.label}>Grafana Password</label>
                  <div style={{ position: 'relative' }}>
                    <input
                      name="grafanaPassword"
                      type={showGrafanaPassword ? "text" : "password"}
                      value={fields.grafanaPassword}
                      onChange={handleChange}
                      style={{
                        ...formStyles.input,
                        paddingRight: '45px'
                      }}
                      placeholder="Enter Grafana admin password"
                    />
                    <button
                      type="button"
                      onClick={() => setShowGrafanaPassword(!showGrafanaPassword)}
                      style={{
                        position: 'absolute',
                        right: '12px',
                        top: '50%',
                        transform: 'translateY(-50%)',
                        background: 'transparent',
                        border: 'none',
                        cursor: 'pointer',
                        padding: '4px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#7f8c8d',
                        fontSize: '18px',
                        transition: 'color 0.2s'
                      }}
                      onMouseEnter={(e) => e.target.style.color = '#2c3e50'}
                      onMouseLeave={(e) => e.target.style.color = '#7f8c8d'}
                      title={showGrafanaPassword ? "Hide password" : "Show password"}
                    >
                      {showGrafanaPassword ? '👁️' : '👁️‍🗨️'}
                    </button>
                  </div>
                  <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                    Password for accessing Grafana dashboard
                  </div>
                </div>
              </div>
            </div>

            {/* Jobs Configuration - Admin Only */}
            {isAdmin && (
              <div style={formStyles.section}>
                <h2 style={formStyles.sectionTitle}>Jobs Configuration</h2>
                <div style={formStyles.fieldGroup}>
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>Clean Orthanc Schedule (Cron)</label>
                    <input
                      name="cleanOrthancSchedule"
                      value={fields.cleanOrthancSchedule}
                      onChange={handleChange}
                      style={formStyles.input}
                      placeholder="e.g., 0 0 * * *"
                    />
                  </div>
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>Backup DB Schedule (Cron)</label>
                    <input
                      name="backupDBSchedule"
                      value={fields.backupDBSchedule}
                      onChange={handleChange}
                      style={formStyles.input}
                      placeholder="e.g., 0 0 * * *"
                    />
                  </div>
                </div>
                <div style={formStyles.fieldGroup}>
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>Backup DB Expiry Days</label>
                    <input
                      name="backupDBExpiryDays"
                      type="number"
                      value={fields.backupDBExpiryDays}
                      onChange={handleChange}
                      style={formStyles.input}
                      min="1"
                      max="365"
                    />
                  </div>
                </div>
              </div>
            )}

            {/* Ingress Configuration - Admin Only */}
            {isAdmin && (
              <div style={formStyles.section}>
                <h2 style={formStyles.sectionTitle}>Ingress Configuration</h2>
                <div style={formStyles.checkboxGroup}>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressFrontend"
                      checked={fields.ingressFrontend}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Frontend</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressBackend"
                      checked={fields.ingressBackend}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Backend</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressOrthanc"
                      checked={fields.ingressOrthanc}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Orthanc</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressPgadmin"
                      checked={fields.ingressPgadmin}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>PgAdmin</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressDicom"
                      checked={fields.ingressDicom}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>DICOM</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressDb"
                      checked={fields.ingressDb}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Database</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressRabbitmq"
                      checked={fields.ingressRabbitmq}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>RabbitMQ</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressGrafana"
                      checked={fields.ingressGrafana}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Grafana</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="ingressPrometheus"
                      checked={fields.ingressPrometheus}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Prometheus</label>
                  </div>
                </div>
              </div>
            )}

            {/* Pause Configuration - Admin Only */}
            {isAdmin && (
              <div style={formStyles.section}>
                <h2 style={formStyles.sectionTitle}>Pause Configuration</h2>
                <div style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                  Enable these checkboxes to disable services. When checked, the service will not be deployed.
                </div>
                <div style={formStyles.checkboxGroup}>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="pauseIngestion"
                      checked={fields.pauseIngestion}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Pause Ingestion</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="pauseAnalytics"
                      checked={fields.pauseAnalytics}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Pause Analytics</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="pauseDtp"
                      checked={fields.pauseDtp}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Pause DTP</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="pauseGrafana"
                      checked={fields.pauseGrafana}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Pause Grafana</label>
                  </div>
                  <div style={formStyles.checkboxField}>
                    <input
                      type="checkbox"
                      name="pausePrometheus"
                      checked={fields.pausePrometheus}
                      onChange={handleChange}
                      style={formStyles.checkbox}
                    />
                    <label style={formStyles.checkboxLabel}>Pause Prometheus</label>
                  </div>
                </div>
              </div>
            )}

            <button
              type="submit"
              disabled={saving || archived}
              style={{
                ...formStyles.saveButton,
                ...((saving || archived) && formStyles.disabledButton)
              }}
            >
              {saving ? 'Saving...' : 'Save Configuration'}
            </button>

            {configurationSaved && (
              <div style={{
                background: '#d4edda',
                border: '1px solid #c3e6cb',
                color: '#155724',
                padding: '12px 16px',
                borderRadius: '8px',
                marginTop: '16px',
                display: 'flex',
                alignItems: 'center',
                gap: '12px'
              }}>
                <span style={{ fontSize: '18px' }}>✅</span>
                <div>
                  <strong>Configuration complete and saved!</strong>
                  <div style={{ fontSize: '14px', marginTop: '4px' }}>
                    All required fields are filled with valid values and saved. License management is now available below.
                  </div>
                </div>
              </div>
            )}
          </form>
          )}

          {activeTab === 'customer' && (
            <>
            <div style={formStyles.form}>
              <h2 style={formStyles.sectionTitle}>Customer Details</h2>
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '-10px', marginBottom: '20px' }}>
                Distribution log entry. All fields are mandatory prior to installation.
              </p>

              {customerLoading ? (
                <div style={formStyles.loading}>Loading customer details…</div>
              ) : (
                <form onSubmit={handleCustomerSave}>
                  <div style={formStyles.fieldGroup}>
                    {customerField('contact_name', 'Contact Name', { required: true, placeholder: 'e.g., Dr. Jane Smith' })}
                    {customerField('institution', 'Institution', { required: true, placeholder: 'e.g., General Hospital' })}
                  </div>

                  <div style={formStyles.fieldGroup}>
                    {customerField('address_line1', 'Address Line 1', { required: true, placeholder: 'Street address', fullWidth: true })}
                    {customerField('address_line2', 'Address Line 2', { placeholder: 'Apt, suite, unit, building (optional)', fullWidth: true })}
                    {customerField('address_line3', 'Address Line 3', { placeholder: 'Optional', fullWidth: true })}
                  </div>

                  <div style={formStyles.fieldGroup}>
                    {customerField('city', 'City', { required: true, placeholder: 'e.g., Toronto' })}
                    {customerField('state_province', 'State / Province', { required: true, placeholder: 'e.g., Ontario' })}
                    {customerField('postal_code', 'Postal Code / Zip Code', { required: true, placeholder: 'e.g., M5V 2T6' })}
                    {customerField('country', 'Country', { required: true, placeholder: 'Select country', options: COUNTRIES })}
                  </div>

                  <div style={formStyles.fieldGroup}>
                    {customerField('phone', 'Phone', { required: true, type: 'tel', placeholder: 'e.g., +1 (555) 123-4567' })}
                    {customerField('email', 'Email', { required: true, type: 'email', placeholder: 'e.g., contact@hospital.org' })}
                  </div>

                  <div style={formStyles.fieldGroup}>
                    <div style={{ ...formStyles.field, gridColumn: '1 / -1' }}>
                      <label style={formStyles.label}>Additional Details</label>
                      <textarea
                        name="additional_details"
                        value={customerForm.additional_details}
                        onChange={handleCustomerChange}
                        disabled={archived}
                        placeholder="Optional — anything else worth recording about this customer (preferred contact times, secondary contacts, etc.)"
                        style={{
                          ...formStyles.input,
                          minHeight: '100px',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                          ...(archived ? { background: '#f1f3f5', cursor: 'not-allowed' } : {}),
                        }}
                      />
                    </div>
                  </div>

                  {customerError && <div style={formStyles.error}>{customerError}</div>}
                  {customerSavedMsg && (
                    <div style={{
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      color: '#155724',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      margin: '12px 0',
                    }}>{customerSavedMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={customerSaving || archived}
                    style={{
                      ...formStyles.saveButton,
                      ...((customerSaving || archived) ? formStyles.disabledButton : {}),
                    }}
                  >
                    {customerSaving ? 'Saving…' : 'Save Customer Details'}
                  </button>

                  {customerDetails && customerDetails.last_updated_at && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '12px', textAlign: 'right' }}>
                      Last updated by <strong>{customerDetails.last_updated_by || 'unknown'}</strong> on {formatDateTime(customerDetails.last_updated_at)}
                    </div>
                  )}

                  {(() => {
                    const history = Array.isArray(customerDetails?.history) ? customerDetails.history : [];
                    const hasCurrent = !!customerDetails?.last_updated_at;
                    if (!hasCurrent && history.length === 0) return null;

                    const rows = [];
                    if (hasCurrent) {
                      rows.push({
                        when: customerDetails.last_updated_at,
                        who: customerDetails.last_updated_by,
                        contact_name: customerDetails.contact_name,
                        institution: customerDetails.institution,
                        address: formatCustomerAddress(customerDetails),
                        country: customerDetails.country,
                        phone: customerDetails.phone,
                        email: customerDetails.email,
                        additional_details: customerDetails.additional_details,
                        current: true,
                      });
                    }
                    history.forEach((h) => rows.push({
                      when: h.edited_at,
                      who: h.edited_by,
                      contact_name: h.contact_name,
                      institution: h.institution,
                      address: formatCustomerAddress(h),
                      country: h.country,
                      phone: h.phone,
                      email: h.email,
                      additional_details: h.additional_details,
                      current: false,
                    }));

                    const thStyle = {
                      textAlign: 'left',
                      padding: '10px 12px',
                      fontSize: '11px',
                      fontWeight: 600,
                      textTransform: 'uppercase',
                      letterSpacing: '0.06em',
                      color: '#7f8c8d',
                      borderBottom: '1px solid #e1e5e9',
                      background: '#f8f9fa',
                      whiteSpace: 'nowrap',
                    };
                    const tdStyle = {
                      padding: '10px 12px',
                      fontSize: '13px',
                      color: '#2c3e50',
                      borderBottom: '1px solid #ecf0f1',
                      verticalAlign: 'top',
                      maxWidth: '240px',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                    };

                    return (
                      <div style={{ marginTop: '28px', borderTop: '1px solid #ecf0f1', paddingTop: '20px' }}>
                        <h3 style={{ fontSize: '15px', fontWeight: 600, color: '#2c3e50', margin: '0 0 12px 0' }}>
                          Edit history
                          <span style={{ color: '#7f8c8d', fontWeight: 400, marginLeft: 8, fontSize: '13px' }}>
                            ({rows.length} entr{rows.length === 1 ? 'y' : 'ies'})
                          </span>
                        </h3>
                        <div style={{ border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'auto', maxHeight: '360px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                <th style={thStyle}>When</th>
                                <th style={thStyle}>Edited By</th>
                                <th style={thStyle}>Contact</th>
                                <th style={thStyle}>Institution</th>
                                <th style={thStyle}>Address</th>
                                <th style={thStyle}>Country</th>
                                <th style={thStyle}>Phone</th>
                                <th style={thStyle}>Email</th>
                                <th style={thStyle}>Additional Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => (
                                <tr key={i} style={r.current ? { background: '#eef7ff' } : {}}>
                                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
                                    {r.when ? formatDateTime(r.when) : '—'}
                                    {r.current && (
                                      <span style={{
                                        marginLeft: 6,
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: '#1c6dd0',
                                        background: '#d6e8ff',
                                        padding: '2px 6px',
                                        borderRadius: '999px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                      }}>Current</span>
                                    )}
                                  </td>
                                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>{r.who || 'unknown'}</td>
                                  <td style={tdStyle} title={r.contact_name || ''}>{r.contact_name || '—'}</td>
                                  <td style={tdStyle} title={r.institution || ''}>{r.institution || '—'}</td>
                                  <td style={tdStyle} title={r.address || ''}>{r.address || '—'}</td>
                                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} title={r.country || ''}>{r.country || '—'}</td>
                                  <td style={{ ...tdStyle, whiteSpace: 'nowrap' }} title={r.phone || ''}>{r.phone || '—'}</td>
                                  <td style={tdStyle} title={r.email || ''}>{r.email || '—'}</td>
                                  <td style={{ ...tdStyle, whiteSpace: 'pre-wrap' }} title={r.additional_details || ''}>{r.additional_details || '—'}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </form>
              )}
            </div>

            {/* Danger zone — archive (decommission) this repository */}
            {!customerLoading && (
              <div style={{
                ...formStyles.form,
                marginTop: '20px',
                border: '1px solid #f5c6cb',
                background: archived ? '#fff5f5' : 'white',
              }}>
                <h2 style={{ ...formStyles.sectionTitle, color: '#c0392b', borderBottomColor: '#e74c3c' }}>
                  {archived ? 'Repository Archived' : 'Archive Repository'}
                </h2>
                {archived ? (
                  <div>
                    <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '0 0 12px 0' }}>
                      This repository has been archived and is now read-only. No further changes can be made.
                    </p>
                    <div style={{
                      background: '#f8d7da',
                      border: '1px solid #f5c6cb',
                      color: '#721c24',
                      borderRadius: '8px',
                      padding: '12px 16px',
                      fontSize: '13px',
                    }}>
                      Archived by <strong>{archivedBy || 'unknown'}</strong>
                      {archivedAt ? <> on {formatDateTime(archivedAt)}</> : null}
                    </div>
                  </div>
                ) : (
                  <div style={{ textAlign: 'center' }}>
                    <p style={{ color: '#7f8c8d', fontSize: '14px', margin: '0 0 16px 0' }}>
                      Archiving marks this customer site as decommissioned. This action cannot be undone,
                      and no further changes can be made to this repository afterward.
                    </p>
                    <button
                      type="button"
                      onClick={() => { setArchiveError(null); setShowArchiveModal(true); }}
                      style={{
                        background: '#e74c3c',
                        color: 'white',
                        border: 'none',
                        borderRadius: '8px',
                        padding: '14px 28px',
                        cursor: 'pointer',
                        fontWeight: 700,
                        fontSize: '15px',
                      }}
                    >
                      Archive this Repository
                    </button>
                  </div>
                )}
              </div>
            )}
            </>
          )}

          {activeTab === 'postinstall' && (
            <div style={formStyles.form}>
              <h2 style={formStyles.sectionTitle}>Post-Installation</h2>
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '-10px', marginBottom: '20px' }}>
                Record the steps completed after Rho is installed at the customer site. Defaults are pre-filled — edit any values that differ at this site.
              </p>

              {postInstallLoading ? (
                <div style={formStyles.loading}>Loading post-installation record…</div>
              ) : (
                <form onSubmit={handlePostInstallSave}>

                  {/* Section: PACS Configuration */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>PACS Configuration</h3>
                    <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                      Record the C-FIND and C-STORE PACS used at this site. Confirm C-ECHO is successful in <em>Rho Console → Settings → PACS Settings</em>.
                    </p>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <fieldset style={{ border: '1px solid #ecf0f1', borderRadius: '8px', padding: '14px 16px' }}>
                        <legend style={{ padding: '0 8px', color: '#2c3e50', fontWeight: 600, fontSize: '13px' }}>C-FIND PACS</legend>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>IP Address</label>
                          <input type="text" value={postInstallForm.cfindIp} onChange={(e) => handlePostInstallTopChange('cfindIp', e.target.value)} placeholder="e.g., 10.20.30.40" style={formStyles.input} />
                        </div>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>Port</label>
                          <input type="text" value={postInstallForm.cfindPort} onChange={(e) => handlePostInstallTopChange('cfindPort', e.target.value)} placeholder="e.g., 104" style={formStyles.input} />
                        </div>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>AET Title</label>
                          <input type="text" value={postInstallForm.cfindAet} onChange={(e) => handlePostInstallTopChange('cfindAet', e.target.value)} placeholder="e.g., CUSTOMER_PACS" style={formStyles.input} />
                        </div>
                      </fieldset>
                      <fieldset style={{ border: '1px solid #ecf0f1', borderRadius: '8px', padding: '14px 16px' }}>
                        <legend style={{ padding: '0 8px', color: '#2c3e50', fontWeight: 600, fontSize: '13px' }}>C-STORE PACS</legend>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>IP Address</label>
                          <input type="text" value={postInstallForm.cstoreIp} onChange={(e) => handlePostInstallTopChange('cstoreIp', e.target.value)} placeholder="e.g., 10.20.30.41" style={formStyles.input} />
                        </div>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>Port</label>
                          <input type="text" value={postInstallForm.cstorePort} onChange={(e) => handlePostInstallTopChange('cstorePort', e.target.value)} placeholder="e.g., 104" style={formStyles.input} />
                        </div>
                        <div style={formStyles.field}>
                          <label style={formStyles.label}>AET Title</label>
                          <input type="text" value={postInstallForm.cstoreAet} onChange={(e) => handlePostInstallTopChange('cstoreAet', e.target.value)} placeholder="e.g., CUSTOMER_STORE" style={formStyles.input} />
                        </div>
                      </fieldset>
                    </div>
                  </div>

                  {/* Section: Configurables Table */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>Configurables</h3>
                    <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                      Defaults are pre-populated. Update the <strong>Value at this site</strong> column to record what was actually set.
                    </p>
                    <div style={{ border: '1px solid #e1e5e9', borderRadius: '8px', maxHeight: '520px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                        <colgroup>
                          <col style={{ width: '24%' }} />
                          <col style={{ width: '46%' }} />
                          <col style={{ width: '12%' }} />
                          <col style={{ width: '18%' }} />
                        </colgroup>
                        <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                          <tr>
                            {['Configurable', 'Description', 'Default', 'Value at this site'].map((h) => (
                              <th key={h} style={{
                                textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                                textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                                borderBottom: '1px solid #e1e5e9', background: '#f8f9fa',
                              }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {POST_INSTALL_CONFIGURABLES.map((c) => {
                            const current = postInstallForm.configurables?.[c.key];
                            const cellStyle = { padding: '8px 12px', verticalAlign: 'top', borderBottom: '1px solid #ecf0f1', color: '#2c3e50', wordBreak: 'break-word' };
                            return (
                              <tr key={c.key}>
                                <td style={{ ...cellStyle, fontWeight: 600 }}>{c.label}</td>
                                <td style={{ ...cellStyle, color: '#7f8c8d' }}>{c.desc || '—'}</td>
                                <td style={{ ...cellStyle, fontFamily: 'monospace', fontSize: '12px', color: '#7f8c8d' }}>
                                  {typeof c.defaultValue === 'boolean' ? (c.defaultValue ? 'true' : 'false') : (c.defaultValue === '' ? '—' : String(c.defaultValue))}
                                </td>
                                <td style={cellStyle}>
                                  {c.type === 'boolean' ? (
                                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                                      <input type="checkbox" checked={!!current} onChange={(e) => handleConfigurableChange(c.key, e.target.checked)} />
                                      <span style={{ fontSize: '12px', color: '#2c3e50' }}>{current ? 'true' : 'false'}</span>
                                    </label>
                                  ) : c.type === 'textarea' ? (
                                    <textarea
                                      value={current || ''}
                                      onChange={(e) => handleConfigurableChange(c.key, e.target.value)}
                                      style={{ ...formStyles.input, width: '100%', boxSizing: 'border-box', minHeight: '60px', fontFamily: 'inherit', resize: 'vertical' }}
                                    />
                                  ) : (
                                    <input
                                      type={c.type === 'date' ? 'date' : 'text'}
                                      value={current ?? ''}
                                      onChange={(e) => handleConfigurableChange(c.key, e.target.value)}
                                      style={{ ...formStyles.input, width: '100%', boxSizing: 'border-box' }}
                                    />
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>

                  {/* Section: Operation Mode (Step 6.1) */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>Operation Mode</h3>
                    <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                      Select how this customer site sends cases to Rho. Test end-to-end with the customer's PACS before declaring go-live.
                    </p>
                    <div style={{ display: 'flex', gap: '24px', marginBottom: '12px' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                        <input type="radio" name="operationMode" value="pull" checked={postInstallForm.operationMode === 'pull'} onChange={() => handlePostInstallTopChange('operationMode', 'pull')} />
                        Pull mode
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                        <input type="radio" name="operationMode" value="push" checked={postInstallForm.operationMode === 'push'} onChange={() => handlePostInstallTopChange('operationMode', 'push')} />
                        Push mode
                      </label>
                    </div>

                    {postInstallForm.operationMode === 'pull' && (
                      <div style={{ padding: '12px 14px', background: '#eef7ff', border: '1px solid #d6e8ff', borderRadius: '6px', fontSize: '13px', color: '#2c3e50', marginBottom: '12px' }}>
                        Confirm <strong>Test PACS Connection</strong> succeeds in <em>Rho Console → Settings → PACS Settings</em>. Pull a test case via Orthanc (<code style={{ color: '#2c3e50', background: '#eef1f5', padding: '1px 5px', borderRadius: '3px' }}>orthanc.&lt;ip&gt;.nip.io</code>) and verify it appears in <em>Rho Console → Cases</em>. If the customer wants Rho live now, keep pull on and turn <em>Send reports to PACS</em> on.
                      </div>
                    )}

                    {postInstallForm.operationMode === 'push' && (
                      <div style={{ padding: '12px 14px', background: '#eef7ff', border: '1px solid #d6e8ff', borderRadius: '6px', fontSize: '13px', color: '#2c3e50', marginBottom: '12px' }}>
                        Give the customer these Rho PACS details so they can register Rho as a modality and push cases:
                        <div style={{ marginTop: '8px', fontFamily: 'monospace', fontSize: '12px', background: '#fff', border: '1px solid #d6e8ff', borderRadius: '4px', padding: '8px 10px', color: '#2c3e50' }}>
                          IP Address: {fields?.host || '<machine-ip>'}<br />
                          AET Title: {fields?.dicomAet || 'rhopacs'}<br />
                          Port: 4242
                        </div>
                        <div style={{ marginTop: '6px', fontSize: '11px', color: '#7f8c8d' }}>
                          IP and AET come from the <strong>Configuration</strong> tab (host, DICOM AET). Update there if these are wrong.
                        </div>
                      </div>
                    )}

                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50', marginRight: '20px' }}>
                      <input type="checkbox" checked={!!postInstallForm.testSuccessful} onChange={(e) => handlePostInstallTopChange('testSuccessful', e.target.checked)} />
                      End-to-end test successful
                    </label>

                    <div style={{ marginTop: '10px', display: 'flex', gap: '20px' }}>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                        <input type="radio" name="goLive" value="now" checked={postInstallForm.goLive === 'now'} onChange={() => handlePostInstallTopChange('goLive', 'now')} />
                        Go live now
                      </label>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                        <input type="radio" name="goLive" value="later" checked={postInstallForm.goLive === 'later'} onChange={() => handlePostInstallTopChange('goLive', 'later')} />
                        Go live later (pull mode off, send-to-PACS off)
                      </label>
                    </div>
                  </div>

                  {/* Section: Powerscribe Integration (Step 6.2) */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>Powerscribe Integration</h3>
                    <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                      Explain the option to auto-populate x-ray reports through Powerscribe. Follow the User Guide if the customer is interested.
                    </p>
                    <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                      <input type="checkbox" checked={!!postInstallForm.powerscribeDiscussed} onChange={(e) => handlePostInstallTopChange('powerscribeDiscussed', e.target.checked)} />
                      Discussed Powerscribe integration with customer
                    </label>
                  </div>

                  {/* Section: Grafana Alert (Step 7.1) — 16bit admins only */}
                  {isAdmin && (
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>Grafana Cloud Alert</h3>
                      <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                        Navigate to <em>Grafana → Alerting → Alert Rules → Rho → Installs</em>. Duplicate an existing alert, modify the query and alert condition to <code style={{ color: '#2c3e50', background: '#eef1f5', padding: '1px 5px', borderRadius: '3px' }}>cluster = {fields?.name || '<customer-name>'}</code>, and update the Notification Message with the customer name.
                      </p>
                      <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50' }}>
                        <input type="checkbox" checked={!!postInstallForm.grafanaAlertAdded} onChange={(e) => handlePostInstallTopChange('grafanaAlertAdded', e.target.checked)} />
                        Grafana Cloud alert added for this customer
                      </label>
                    </div>
                  )}

                  {/* Section: Training Email (Step 7.2) — jurisdiction-aware */}
                  {(() => {
                    const email = buildTrainingEmail();
                    return (
                      <div style={{ marginBottom: '24px' }}>
                        <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>
                          Send Training Email
                          <span style={{ marginLeft: 8, fontSize: '11px', fontWeight: 700, background: email.isUS ? '#d6e8ff' : '#e8e4ff', color: email.isUS ? '#1c6dd0' : '#5b3fc1', padding: '2px 8px', borderRadius: '999px', letterSpacing: '0.04em', textTransform: 'uppercase' }}>
                            {email.isUS ? 'US template' : 'Non-US template'}
                          </span>
                        </h3>
                        <p style={{ color: '#7f8c8d', fontSize: '13px', margin: '0 0 12px 0' }}>
                          Email body below is selected from the customer's jurisdiction in <code style={{ color: '#2c3e50', background: '#eef1f5', padding: '1px 5px', borderRadius: '3px' }}>rho.yaml</code> ({fields?.jurisdiction || '—'}). Copy and send to the customer's contact email.
                        </p>
                        <div style={{ background: '#f8f9fa', border: '1px solid #e1e5e9', borderRadius: '8px', padding: '14px 16px', position: 'relative' }}>
                          <div style={{ fontSize: '12px', color: '#7f8c8d', marginBottom: '6px' }}><strong style={{ color: '#2c3e50' }}>Subject:</strong> {email.subject}</div>
                          <pre style={{
                            margin: 0,
                            whiteSpace: 'pre-wrap',
                            wordBreak: 'break-word',
                            fontFamily: 'inherit',
                            fontSize: '13px',
                            color: '#2c3e50',
                            lineHeight: 1.55,
                          }}>{email.body}</pre>
                          <button
                            type="button"
                            onClick={async () => {
                              try {
                                await navigator.clipboard.writeText(`Subject: ${email.subject}\n\n${email.body}`);
                                setCopyEmailFeedback('Email copied to clipboard');
                                setTimeout(() => setCopyEmailFeedback(''), 2000);
                              } catch (err) {
                                setCopyEmailFeedback('Copy failed — select the text manually');
                                setTimeout(() => setCopyEmailFeedback(''), 3000);
                              }
                            }}
                            style={{
                              position: 'absolute', top: '10px', right: '10px',
                              padding: '6px 12px', background: '#3498db', color: 'white',
                              border: 'none', borderRadius: '6px', cursor: 'pointer',
                              fontSize: '12px', fontWeight: 600,
                            }}
                          >
                            📋 Copy
                          </button>
                          {copyEmailFeedback && (
                            <div style={{ fontSize: '12px', color: '#27ae60', marginTop: '8px' }}>{copyEmailFeedback}</div>
                          )}
                        </div>
                        <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', cursor: 'pointer', color: '#2c3e50', marginTop: '12px' }}>
                          <input type="checkbox" checked={!!postInstallForm.trainingEmailSent} onChange={(e) => handlePostInstallTopChange('trainingEmailSent', e.target.checked)} />
                          Sent training email to customer
                        </label>
                      </div>
                    );
                  })()}

                  {/* Section: Notes */}
                  <div style={{ marginBottom: '24px' }}>
                    <h3 style={{ color: '#2c3e50', fontSize: '16px', fontWeight: 600, marginBottom: '6px' }}>Additional Notes</h3>
                    <textarea
                      value={postInstallForm.notes}
                      onChange={(e) => handlePostInstallTopChange('notes', e.target.value)}
                      placeholder="Anything else worth recording about this installation…"
                      style={{ ...formStyles.input, minHeight: '120px', fontFamily: 'inherit', resize: 'vertical', width: '100%' }}
                    />
                  </div>

                  {postInstallError && <div style={formStyles.error}>{postInstallError}</div>}
                  {postInstallSavedMsg && (
                    <div style={{
                      background: '#d4edda',
                      border: '1px solid #c3e6cb',
                      color: '#155724',
                      padding: '12px 16px',
                      borderRadius: '8px',
                      margin: '12px 0',
                    }}>{postInstallSavedMsg}</div>
                  )}

                  <button
                    type="submit"
                    disabled={postInstallSaving || archived}
                    style={{
                      ...formStyles.saveButton,
                      ...((postInstallSaving || archived) ? formStyles.disabledButton : {}),
                    }}
                  >
                    {postInstallSaving ? 'Saving…' : 'Save Post-Installation Record'}
                  </button>

                  {postInstall && postInstall.last_updated_at && (
                    <div style={{ fontSize: '12px', color: '#7f8c8d', marginTop: '12px', textAlign: 'right' }}>
                      Last updated by <strong>{postInstall.last_updated_by || 'unknown'}</strong> on {formatDateTime(postInstall.last_updated_at)}
                    </div>
                  )}

                  {(() => {
                    const history = Array.isArray(postInstall?.history) ? postInstall.history : [];
                    const hasCurrent = !!postInstall?.last_updated_at;
                    if (!hasCurrent && history.length === 0) return null;

                    const rows = [];
                    if (hasCurrent) {
                      rows.push({
                        when: postInstall.last_updated_at,
                        who: postInstall.last_updated_by,
                        current: true,
                      });
                    }
                    history.forEach((h) => rows.push({
                      when: h.edited_at,
                      who: h.edited_by,
                      current: false,
                    }));

                    return (
                      <div style={{ marginTop: '28px', borderTop: '1px solid #ecf0f1', paddingTop: '20px' }}>
                        <h3 style={{ color: '#2c3e50', fontSize: '15px', fontWeight: 600, margin: '0 0 12px 0' }}>
                          Edit history
                          <span style={{ color: '#7f8c8d', fontWeight: 400, marginLeft: 8, fontSize: '13px' }}>
                            ({rows.length} entr{rows.length === 1 ? 'y' : 'ies'})
                          </span>
                        </h3>
                        <div style={{ border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'auto', maxHeight: '320px' }}>
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                              <tr>
                                {['When', 'Edited By'].map((h) => (
                                  <th key={h} style={{
                                    textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                                    borderBottom: '1px solid #e1e5e9', background: '#f8f9fa', whiteSpace: 'nowrap',
                                  }}>{h}</th>
                                ))}
                              </tr>
                            </thead>
                            <tbody>
                              {rows.map((r, i) => (
                                <tr key={i} style={r.current ? { background: '#eef7ff' } : {}}>
                                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#2c3e50', borderBottom: '1px solid #ecf0f1' }}>
                                    {r.when ? formatDateTime(r.when) : '—'}
                                    {r.current && (
                                      <span style={{
                                        marginLeft: 6,
                                        fontSize: '10px',
                                        fontWeight: 700,
                                        color: '#1c6dd0',
                                        background: '#d6e8ff',
                                        padding: '2px 6px',
                                        borderRadius: '999px',
                                        textTransform: 'uppercase',
                                        letterSpacing: '0.04em',
                                      }}>Current</span>
                                    )}
                                  </td>
                                  <td style={{ padding: '10px 12px', whiteSpace: 'nowrap', color: '#2c3e50', borderBottom: '1px solid #ecf0f1' }}>
                                    {r.who || 'unknown'}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      </div>
                    );
                  })()}
                </form>
              )}
            </div>
          )}

          {activeTab === 'service' && (
            <div style={formStyles.form}>
              <h2 style={formStyles.sectionTitle}>Service Log</h2>
              <p style={{ color: '#7f8c8d', fontSize: '14px', marginTop: '-10px', marginBottom: '20px' }}>
                Record service activity for this customer — what you investigated, what you changed, anything worth keeping for the next person.
              </p>

              <form onSubmit={handleServiceSave}>
                <div style={{ background: '#f8f9fa', border: '1px solid #ecf0f1', borderRadius: '8px', padding: '12px 14px', marginBottom: '16px', fontSize: '13px', color: '#2c3e50' }}>
                  <div>Performed by <strong>{currentUserEmail || 'unknown'}</strong> &middot; timestamped on save</div>
                  <div style={{ marginTop: '6px' }} title="Unique Device Identifier: GTIN for the jurisdiction plus the Rho version, as shown on the product's About page. Recorded on the entry when you save.">
                    Device UDI{' '}
                    <code style={{ color: '#2c3e50', background: '#eef1f5', padding: '1px 6px', borderRadius: '3px' }}>
                      {udiFor(fields?.jurisdiction, fields?.targetRevision) || 'unknown until jurisdiction and version are set'}
                    </code>
                    <span style={{ color: '#7f8c8d', marginLeft: 8 }}>{fields?.jurisdiction || '—'} · {fields?.targetRevision || '—'}</span>
                  </div>
                </div>

                <div style={formStyles.fieldGroup}>
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>
                      First Name
                      {currentUserClaimNames.first && (
                        <span style={{ marginLeft: 6, fontSize: '11px', color: '#7f8c8d', fontWeight: 400 }}>(from your profile)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="first_name"
                      value={serviceForm.first_name}
                      onChange={handleServiceFormChange}
                      placeholder="e.g., Alex"
                      style={formStyles.input}
                    />
                  </div>
                  <div style={formStyles.field}>
                    <label style={formStyles.label}>
                      Last Name
                      {currentUserClaimNames.last && (
                        <span style={{ marginLeft: 6, fontSize: '11px', color: '#7f8c8d', fontWeight: 400 }}>(from your profile)</span>
                      )}
                    </label>
                    <input
                      type="text"
                      name="last_name"
                      value={serviceForm.last_name}
                      onChange={handleServiceFormChange}
                      placeholder="e.g., Morgan"
                      style={formStyles.input}
                    />
                  </div>
                </div>

                <div style={{ ...formStyles.field, marginBottom: '16px' }}>
                  <label style={formStyles.label}>
                    Service Notes <span style={{ color: '#e74c3c' }}>*</span>
                  </label>
                  <textarea
                    name="notes"
                    value={serviceForm.notes}
                    onChange={handleServiceFormChange}
                    placeholder="What did you investigate, change, or confirm during this service session?"
                    style={{ ...formStyles.input, minHeight: '140px', fontFamily: 'inherit', resize: 'vertical' }}
                    required
                  />
                </div>

                {serviceError && <div style={formStyles.error}>{serviceError}</div>}
                {serviceSavedMsg && (
                  <div style={{
                    background: '#d4edda',
                    border: '1px solid #c3e6cb',
                    color: '#155724',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    margin: '12px 0',
                  }}>{serviceSavedMsg}</div>
                )}

                <button
                  type="submit"
                  disabled={serviceSaving || archived}
                  style={{
                    ...formStyles.saveButton,
                    ...((serviceSaving || archived) ? formStyles.disabledButton : {}),
                  }}
                >
                  {serviceSaving ? 'Saving…' : 'Add Service Log Entry'}
                </button>
              </form>

              <div style={{ marginTop: '28px', borderTop: '1px solid #ecf0f1', paddingTop: '20px' }}>
                <h3 style={{ color: '#2c3e50', fontSize: '15px', fontWeight: 600, margin: '0 0 12px 0' }}>
                  Service history
                  <span style={{ color: '#7f8c8d', fontWeight: 400, marginLeft: 8, fontSize: '13px' }}>
                    ({serviceLog.length} {serviceLog.length === 1 ? 'entry' : 'entries'})
                  </span>
                </h3>
                {serviceLogLoading ? (
                  <div style={formStyles.loading}>Loading service history…</div>
                ) : serviceLog.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: '#7f8c8d', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #ecf0f1' }}>
                    No service activity recorded yet.
                  </div>
                ) : (
                  <div style={{ border: '1px solid #e1e5e9', borderRadius: '8px', maxHeight: '480px', overflowY: 'auto' }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px', tableLayout: 'fixed' }}>
                      <colgroup>
                        <col style={{ width: '16%' }} />
                        <col style={{ width: '20%' }} />
                        <col style={{ width: '22%' }} />
                        <col style={{ width: '42%' }} />
                      </colgroup>
                      <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                        <tr>
                          {['When', 'Performed By', 'Device UDI', 'Notes'].map((h) => (
                            <th key={h} style={{
                              textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                              textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                              borderBottom: '1px solid #e1e5e9', background: '#f8f9fa',
                            }}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {serviceLog.map((entry, i) => {
                          const cell = { padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #ecf0f1', color: '#2c3e50', wordBreak: 'break-word' };
                          const displayName = [entry.first_name, entry.last_name].filter(Boolean).join(' ');
                          return (
                            <tr key={`${entry.service_at}-${i}`}>
                              <td style={cell}>
                                {entry.service_at ? formatDateTime(entry.service_at) : '—'}
                              </td>
                              <td style={cell}>
                                {displayName && (
                                  <div style={{ fontWeight: 600 }}>{displayName}</div>
                                )}
                                <div style={{ color: '#7f8c8d', fontSize: '12px' }}>{entry.email || 'unknown'}</div>
                              </td>
                              <td style={{ ...cell, fontFamily: 'source-code-pro, Menlo, Monaco, Consolas, monospace', fontSize: '12px' }}>
                                {entry.udi ? (
                                  <>
                                    <div>{entry.udi}</div>
                                    <div style={{ color: '#7f8c8d', fontFamily: 'inherit' }}>{[entry.jurisdiction, entry.rho_version].filter(Boolean).join(' · ')}</div>
                                  </>
                                ) : (
                                  <span style={{ color: '#7f8c8d' }} title="Recorded before UDI capture; shown from the repository's current jurisdiction and version">
                                    {udiFor(fields?.jurisdiction, fields?.targetRevision) || '—'}
                                  </span>
                                )}
                              </td>
                              <td style={{ ...cell, whiteSpace: 'pre-wrap' }}>{entry.notes || '—'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </div>
          )}

          {activeTab === 'readme' && isAdmin && (
            <ReadmeEditor repo={repo} readOnly={archived} />
          )}

          {activeTab === 'rawyaml' && isAdmin && (
            <RawYamlEditor repo={repo} readOnly={archived} onSaved={() => fetchYaml({ silent: true })} />
          )}
        </>
      )}

      {activeTab === 'config' && (
      <>
      {/* License Management - Only accessible after configuration is saved */}
      {configurationSaved ? (
        <div style={{
          ...formStyles.form,
          marginTop: '20px'
        }}>
          <h2 style={formStyles.sectionTitle}>License Management</h2>
          <LicenseRequest
            repo={repo}
            isAdmin={isAdmin}
            onLicenseGenerated={handleLicenseGenerated}
            disabled={archived}
          />
        </div>
      ) : (
        <div style={{
          ...formStyles.form,
          marginTop: '20px',
          background: '#f8f9fa',
          border: '1px solid #dee2e6'
        }}>
          <div style={{
            textAlign: 'center',
            padding: '40px 20px',
            color: '#6c757d'
          }}>
            <div style={{
              fontSize: '48px',
              marginBottom: '16px'
            }}>
              🔒
            </div>
            <h3 style={{
              margin: '0 0 12px 0',
              color: '#495057',
              fontSize: '18px',
              fontWeight: '600'
            }}>
              License Management Locked
            </h3>
            <p style={{
              margin: '0 0 20px 0',
              fontSize: '14px',
              lineHeight: '1.5'
            }}>
              Please fill in all required fields with valid values (no default placeholders) and save your configuration to access license management features.
            </p>
            <div style={{
              fontSize: '12px',
              color: '#adb5bd',
              fontStyle: 'italic'
            }}>
              All fields must contain actual values, not template placeholders like &lt;machine-ip&gt; or &lt;cluster-name&gt;.
            </div>
          </div>
        </div>
      )}

      {/* Deploy Key Management - Outside the form */}
      <div style={{
        ...formStyles.form,
        marginTop: '20px'
      }}>
        <h2 style={formStyles.sectionTitle}>Deploy Key Management</h2>

        {/* Error and Success Messages */}
        {deployKeyError && (
          <div style={{
            color: '#e74c3c',
            background: '#fdf2f2',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #fecaca',
            fontSize: '14px'
          }}>
            {deployKeyError}
          </div>
        )}

        {deployKeySuccess && (
          <div style={{
            color: '#27ae60',
            background: '#f0fff4',
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            border: '1px solid #c6f6d5',
            fontSize: '14px'
          }}>
            {deployKeySuccess}
          </div>
        )}

        {/* Current Deploy Keys */}
        <div style={{ marginBottom: '30px' }}>
          <h3 style={{
            color: '#2c3e50',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '15px'
          }}>
            Current Deploy Keys
          </h3>

          {deployKeysLoading ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#7f8c8d',
              fontSize: '14px'
            }}>
              Loading deploy keys...
            </div>
          ) : deployKeys.length === 0 ? (
            <div style={{
              textAlign: 'center',
              padding: '20px',
              color: '#95a5a6',
              fontSize: '14px',
              background: '#f8f9fa',
              borderRadius: '8px',
              border: '1px solid #e1e5e9'
            }}>
              No deploy keys found for this repository
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
              {deployKeys.map((key) => (
                <div key={key.id} style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  padding: '15px',
                  background: '#f8f9fa',
                  borderRadius: '8px',
                  border: '1px solid #e1e5e9'
                }}>
                  <div>
                    <div style={{
                      fontWeight: '600',
                      color: '#2c3e50',
                      marginBottom: '5px'
                    }}>
                      {key.title}
                    </div>
                    <div style={{
                      fontSize: '12px',
                      color: '#7f8c8d',
                      fontFamily: 'monospace',
                      wordBreak: 'break-all'
                    }}>
                      {key.key.substring(0, 50)}...
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeDeployKey(key.id)}
                    style={{
                      background: '#e74c3c',
                      color: 'white',
                      border: 'none',
                      borderRadius: '6px',
                      padding: '8px 16px',
                      cursor: 'pointer',
                      fontSize: '12px',
                      fontWeight: '500',
                      transition: 'background 0.2s'
                    }}
                    title="Remove this deploy key"
                  >
                    Remove
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Add New Deploy Key */}
        <div>
          <h3 style={{
            color: '#2c3e50',
            fontSize: '16px',
            fontWeight: '600',
            marginBottom: '15px'
          }}>
            Add New Deploy Key
          </h3>

          <div style={formStyles.fieldGroup}>
            <div style={formStyles.field}>
              <label style={formStyles.label}>Key Title</label>
              <input
                type="text"
                value={newDeployKey.title}
                onChange={(e) => setNewDeployKey(prev => ({ ...prev, title: e.target.value }))}
                style={formStyles.input}
                placeholder="e.g., ArgoCD SSH Key"
              />
            </div>
            <div style={formStyles.field}>
              <label style={formStyles.label}>SSH Public Key</label>
              <textarea
                value={newDeployKey.key}
                onChange={(e) => setNewDeployKey(prev => ({ ...prev, key: e.target.value }))}
                style={{
                  ...formStyles.input,
                  minHeight: '80px',
                  fontFamily: 'monospace',
                  fontSize: '12px'
                }}
                placeholder="ssh-rsa AAAAB3NzaC1yc2EAAAADAQABAAABAQC..."
              />
            </div>
          </div>

          <button
            type="button"
            onClick={addDeployKey}
            disabled={archived}
            style={{
              background: archived ? '#bdc3c7' : '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              padding: '12px 24px',
              cursor: archived ? 'not-allowed' : 'pointer',
              fontWeight: '600',
              fontSize: '14px',
              transition: 'background 0.2s'
            }}
          >
            Add Deploy Key
          </button>
        </div>
      </div>

      {/* Configuration audit log */}
      <div style={{ ...formStyles.form, marginTop: '20px' }}>
        <h2 style={formStyles.sectionTitle}>Change History</h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '12px', flexWrap: 'wrap', marginTop: '-10px', marginBottom: '16px' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: '8px', fontSize: '13px', color: '#2c3e50', cursor: 'pointer', userSelect: 'none' }}>
            <input
              type="checkbox"
              checked={auditVersionOnly}
              onChange={(e) => setAuditVersionOnly(e.target.checked)}
              style={{ accentColor: '#3498db', width: '16px', height: '16px', cursor: 'pointer' }}
            />
            Version changes only
          </label>
        </div>
        {(() => {
          const VERSION_PATH = 'spec.source.targetRevision';
          const visibleAudit = auditVersionOnly
            ? configAudit.filter((e) => e && e.changes && Object.prototype.hasOwnProperty.call(e.changes, VERSION_PATH))
            : configAudit;
          return (
        <>
        {configAuditLoading ? (
          <div style={formStyles.loading}>Loading change history…</div>
        ) : visibleAudit.length === 0 ? (
          <div style={{ padding: '24px', textAlign: 'center', color: '#7f8c8d', background: '#f8f9fa', borderRadius: '8px', border: '1px solid #ecf0f1' }}>
            {configAudit.length === 0
              ? 'No edits recorded yet.'
              : 'No version changes recorded yet. Uncheck "Version changes only" to see all edits.'}
          </div>
        ) : (
          <div style={{ border: '1px solid #e1e5e9', borderRadius: '8px', overflow: 'auto', maxHeight: '480px' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
                <tr>
                  <th style={{
                    textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                    borderBottom: '1px solid #e1e5e9', background: '#f8f9fa', whiteSpace: 'nowrap',
                  }}>When</th>
                  <th style={{
                    textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                    borderBottom: '1px solid #e1e5e9', background: '#f8f9fa', whiteSpace: 'nowrap',
                  }}>Edited By</th>
                  <th style={{
                    textAlign: 'left', padding: '10px 12px', fontSize: '11px', fontWeight: 600,
                    textTransform: 'uppercase', letterSpacing: '0.06em', color: '#7f8c8d',
                    borderBottom: '1px solid #e1e5e9', background: '#f8f9fa',
                  }}>Changes</th>
                </tr>
              </thead>
              <tbody>
                {visibleAudit.map((entry, idx) => {
                  const changes = entry.changes || {};
                  const paths = Object.keys(changes);
                  // Strip the verbose helm valuesObject prefix for readability
                  const shortenPath = (p) => p
                    .replace(/^spec\.source\.helm\.valuesObject\./, '')
                    .replace(/^spec\.source\./, '');
                  const fmtVal = (v) => {
                    if (v === null || v === undefined || v === '') return <em style={{ color: '#95a5a6' }}>—</em>;
                    if (typeof v === 'boolean') return v ? 'true' : 'false';
                    if (typeof v === 'object') return JSON.stringify(v);
                    return String(v);
                  };
                  return (
                    <tr key={`${entry.edited_at}-${idx}`}>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #ecf0f1', whiteSpace: 'nowrap', color: '#2c3e50' }}>
                        {entry.edited_at ? formatDateTime(entry.edited_at) : '—'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #ecf0f1', whiteSpace: 'nowrap', color: '#2c3e50' }}>
                        {entry.edited_by || 'unknown'}
                      </td>
                      <td style={{ padding: '10px 12px', verticalAlign: 'top', borderBottom: '1px solid #ecf0f1', color: '#2c3e50' }}>
                        {paths.length === 0 ? (
                          <em style={{ color: '#95a5a6' }}>no recorded changes</em>
                        ) : (
                          <ul style={{ margin: 0, paddingLeft: '18px' }}>
                            {paths.map((p) => (
                              <li key={p} style={{ marginBottom: '4px', lineHeight: 1.4 }}>
                                <code style={{ background: '#eef1f5', color: '#2c3e50', padding: '1px 6px', borderRadius: '4px', fontSize: '12px' }}>
                                  {shortenPath(p)}
                                </code>
                                <span style={{ margin: '0 6px', color: '#7f8c8d' }}>:</span>
                                <span style={{ color: '#c0392b' }}>{fmtVal(changes[p].from)}</span>
                                <span style={{ margin: '0 6px', color: '#7f8c8d' }}>→</span>
                                <span style={{ color: '#27ae60' }}>{fmtVal(changes[p].to)}</span>
                              </li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        </>
          );
        })()}
      </div>
      </>
      )}

      {/* Archive confirmation modal */}
      {showArchiveModal && (
        <div
          onClick={() => !archiving && setShowArchiveModal(false)}
          style={{
            position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            zIndex: 1000, padding: '20px',
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              background: 'white', borderRadius: '12px', padding: '28px',
              maxWidth: '600px', width: '100%', boxShadow: '0 20px 60px rgba(0,0,0,0.3)',
            }}
          >
            <h2 style={{ margin: '0 0 16px 0', color: '#c0392b', fontSize: '20px', fontWeight: 700 }}>
              Archive {repo}?
            </h2>
            <p style={{ color: '#2c3e50', fontSize: '14px', lineHeight: 1.6, margin: '0 0 12px 0' }}>
              <strong>This action cannot be undone.</strong> No further changes can be made to this
              repository.
            </p>
            <p style={{ color: '#2c3e50', fontSize: '14px', lineHeight: 1.6, margin: '0 0 12px 0' }}>
              By clicking the button below, you are attesting to the Rho software being uninstalled at
              this customer site by executing the following command in the terminal (on the customer's
              machine):
            </p>
            <div style={{
              background: '#2c3e50',
              borderRadius: '8px',
              padding: '14px 16px',
              marginBottom: '16px',
              position: 'relative',
            }}>
              <code style={{
                color: '#ecf0f1',
                fontFamily: 'Consolas, Monaco, "Courier New", monospace',
                fontSize: '14px',
                background: 'transparent',
                padding: 0,
              }}>
                k3s-uninstall.sh
              </code>
              <button
                type="button"
                onClick={() => { navigator.clipboard.writeText('k3s-uninstall.sh'); }}
                style={{
                  position: 'absolute', top: '10px', right: '10px',
                  background: '#3498db', color: 'white', border: 'none',
                  borderRadius: '6px', padding: '6px 12px', cursor: 'pointer',
                  fontSize: '12px', fontWeight: 500,
                }}
              >
                Copy
              </button>
            </div>

            {archiveError && (
              <div style={{
                color: '#e74c3c', background: '#fdf2f2', padding: '12px 14px',
                borderRadius: '8px', marginBottom: '16px', border: '1px solid #fecaca', fontSize: '13px',
              }}>
                {archiveError}
              </div>
            )}

            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button
                type="button"
                onClick={() => setShowArchiveModal(false)}
                disabled={archiving}
                style={{
                  background: '#ecf0f1', color: '#2c3e50', border: 'none',
                  borderRadius: '8px', padding: '12px 22px', cursor: archiving ? 'not-allowed' : 'pointer',
                  fontWeight: 600, fontSize: '14px',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleArchive}
                disabled={archiving}
                style={{
                  background: '#e74c3c', color: 'white', border: 'none',
                  borderRadius: '8px', padding: '12px 22px', cursor: archiving ? 'not-allowed' : 'pointer',
                  fontWeight: 700, fontSize: '14px', opacity: archiving ? 0.6 : 1,
                }}
              >
                {archiving ? 'Archiving…' : 'I attest — Archive Repository'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}